import { Namespace, BlankNode, NamedNode, Fetcher, Serializer, IndexedFormula, uri, Literal, Statement, graph, parse } from 'rdflib';
import { BAD_REQUEST, NON_AUTHORITATIVE_INFORMATION, INTERNAL_SERVER_ERROR, NOT_FOUND } from 'http-status-codes';
import { NQuadsParser } from 'n-quads-parser';

const F_NTRIPLES = "application/n-triples";
const F_NQUADS = "application/n-quads";
const F_TURTLE = "text/turtle";
const F_N3 = "text/n3";
const F_PLAIN = "text/plain";
const F_JSON = "application/json";
const F_JSONLD = "application/ld+json";
const F_RDF_XML = "application/rdf+xml";
const F_NTRIPLES_DEP = "text/ntriples";
const F_TURTLE_DEP = "application/x-turtle";
const NON_CONTENT_EXTS = ["php", "asp", "aspx", "cgi", "jsp"];
// From https://www.w3.org/ns/activitystreams-owl
const as = Namespace("https://www.w3.org/ns/activitystreams#", [
    "actor", "attributedTo", "attachment", "attachments", "author", "bcc", "bto", "cc", "context", "current",
    "first", "generator", "icon", "image", "inReplyTo", "items", "last", "location", "next", "object", "oneOf",
    "anyOf", "prev", "preview", "provider", "replies", "result", "audience", "partOf", "tag", "tags", "target",
    "origin", "instrument", "to", "url", "subject", "relationship", "describes", "formerType", "accuracy",
    "altitude", "content", "name", "downstreamDuplicates", "duration", "endTime", "height", "href", "hreflang",
    "id", "latitude", "longitude", "mediaType", "objectType", "published", "radius", "rating", "rel", "startIndex",
    "startTime", "summary", "totalItems", "units", "updated", "upstreamDuplicates", "verb", "width", "deleted",
    "Accept", "Activity", "Block", "IntransitiveActivity", "Add", "Announce", "Application", "Arrive", "Article",
    "Audio", "Collection", "CollectionPage", "OrderedCollectionPage", "Relationship", "Create", "Delete", "Dislike",
    "Document", "Event", "Flag", "Follow", "Group", "Ignore", "Image", "Invite", "Join", "Leave", "Like", "View",
    "Listen", "Read", "Move", "Travel", "Link", "Mention", "Note", "Object", "Offer", "OrderedCollection",
    "OrderedItems", "Page", "Person", "Organization", "Profile", "Place", "Question", "Reject", "Remove", "Service",
    "TentativeAccept", "TentativeReject", "Tombstone", "Undo", "Update", "Video",
]);
// From http://dublincore.org/specifications/dublin-core/dcmi-terms/2012-06-14/
const dc = Namespace("http://purl.org/dc/terms/", [
    // Properties
    "abstract", "accessRights", "accrualMethod", "accrualPeriodicity", "accrualPolicy", "alternative", "audience",
    "available", "bibliographicCitation", "conformsTo", "contributor", "coverage", "created", "creator", "date",
    "dateAccepted", "dateCopyrighted", "dateSubmitted", "description", "educationLevel", "extent", "format",
    "hasFormat", "hasPart", "hasVersion", "identifier", "instructionalMethod", "isFormatOf", "isPartOf",
    "isReferencedBy", "isReplacedBy", "isRequiredBy", "issued", "isVersionOf", "language", "license", "mediator",
    "medium", "modified", "provenance", "publisher", "references", "relation", "replaces", "requires", "rights",
    "rightsHolder", "source", "spatial", "subject", "tableOfContents", "temporal", "title", "type", "valid",
    // Vocabulary Encoding Schemes
    "DCMIType", "DDC", "IMT", "LCC", "LCSH", "MESH", "NLM", "TGN", "UDC",
    // Classes
    "Agent", "AgentClass", "BibliographicResource", "FileFormat", "Frequency", "Jurisdiction", "LicenseDocument",
    "LinguisticSystem", "Location", "LocationPeriodOrJurisdiction", "MediaType", "MediaTypeOrExtent",
    "MethodOfAccrual", "MethodOfInstruction", "PeriodOfTime", "PhysicalMedium", "PhysicalResource", "Policy",
    "ProvenanceStatement", "RightsStatement", "SizeOrDuration", "Standard",
]);
// From https://www.w3.org/1999/02/22-rdf-syntax-ns#
const rdf = Namespace("http://www.w3.org/1999/02/22-rdf-syntax-ns#", [
    "HTML", "langString", "PlainLiteral", "type", "Property", "Statement", "subject", "predicate", "object",
    "Bag", "Seq", "Alt", "value", "List", "nil", "first", "rest", "XMLLiteral",
]);
// From https://www.w3.org/2000/01/rdf-schema#
const rdfs = Namespace("http://www.w3.org/2000/01/rdf-schema#", [
    "Resource", "Class", "subClassOf", "subPropertyOf", "comment", "label", "domain", "range", "seeAlso",
    "isDefinedBy", "Literal", "Container", "ContainerMembershipProperty", "member", "Datatype",
]);
// From https://schema.org/docs/developers.html#defs
const schema = Namespace("http://schema.org/", [
    "AMRadioChannel", "APIReference", "AboutPage", "AcceptAction", "Accommodation", "AccountingService",
    "AchieveAction", "Action", "ActionAccessSpecification", "ActionStatusType", "ActivateAction", "AddAction",
    "AdministrativeArea", "AdultEntertainment", "AggregateOffer", "AggregateRating", "AgreeAction", "Airline",
    "Airport", "AlignmentObject", "AllocateAction", "AmusementPark", "AnimalShelter", "Answer", "Apartment",
    "ApartmentComplex", "AppendAction", "ApplyAction", "Aquarium", "ArriveAction", "ArtGallery", "Article",
    "AskAction", "AssessAction", "AssignAction", "Attorney", "Audience", "AudioObject", "AuthorizeAction",
    "AutoBodyShop", "AutoDealer", "AutoPartsStore", "AutoRental", "AutoRepair", "AutoWash", "AutomatedTeller",
    "AutomotiveBusiness", "Bakery", "BankAccount", "BankOrCreditUnion", "BarOrPub", "Barcode", "Beach",
    "BeautySalon", "BedAndBreakfast", "BedDetails", "BedType", "BefriendAction", "BikeStore", "Blog", "BlogPosting",
    "BoardingPolicyType", "BodyOfWater", "Book", "BookFormatType", "BookSeries", "BookStore", "BookmarkAction",
    "Boolean", "BorrowAction", "BowlingAlley", "Brand", "BreadcrumbList", "Brewery", "Bridge", "BroadcastChannel",
    "BroadcastEvent", "BroadcastFrequencySpecification", "BroadcastService", "BuddhistTemple", "BusReservation",
    "BusStation", "BusStop", "BusTrip", "BusinessAudience", "BusinessEntityType", "BusinessEvent",
    "BusinessFunction", "BuyAction", "CableOrSatelliteService", "CafeOrCoffeeShop", "Campground", "CampingPitch",
    "Canal", "CancelAction", "Car", "Casino", "CatholicChurch", "Cemetery", "CheckAction", "CheckInAction",
    "CheckOutAction", "CheckoutPage", "ChildCare", "ChildrensEvent", "ChooseAction", "Church", "City", "CityHall",
    "CivicStructure", "ClaimReview", "Clip", "ClothingStore", "Code", "CollectionPage", "CollegeOrUniversity",
    "ComedyClub", "ComedyEvent", "Comment", "CommentAction", "CommunicateAction", "CompoundPriceSpecification",
    "ComputerLanguage", "ComputerStore", "ConfirmAction", "ConsumeAction", "ContactPage", "ContactPoint",
    "ContactPointOption", "Continent", "ControlAction", "ConvenienceStore", "Conversation", "CookAction",
    "Corporation", "Country", "Course", "CourseInstance", "Courthouse", "CreateAction", "CreativeWork",
    "CreativeWorkSeason", "CreativeWorkSeries", "CreditCard", "Crematorium", "CurrencyConversionService",
    "DanceEvent", "DanceGroup", "DataCatalog", "DataDownload", "DataFeed", "DataFeedItem", "DataType", "Dataset",
    "Date", "DateTime", "DatedMoneySpecification", "DayOfWeek", "DaySpa", "DeactivateAction",
    "DefenceEstablishment", "DeleteAction", "DeliveryChargeSpecification", "DeliveryEvent", "DeliveryMethod",
    "Demand", "Dentist", "DepartAction", "DepartmentStore", "DepositAccount", "DigitalDocument",
    "DigitalDocumentPermission", "DigitalDocumentPermissionType", "DisagreeAction", "DiscoverAction",
    "DiscussionForumPosting", "DislikeAction", "Distance", "Distillery", "DonateAction", "DownloadAction",
    "DrawAction", "DrinkAction", "DriveWheelConfigurationValue", "DryCleaningOrLaundry", "Duration", "EatAction",
    "EducationEvent", "EducationalAudience", "EducationalOrganization", "Electrician", "ElectronicsStore",
    "ElementarySchool", "EmailMessage", "Embassy", "EmergencyService", "EmployeeRole", "EmployerAggregateRating",
    "EmploymentAgency", "EndorseAction", "EndorsementRating", "Energy", "EngineSpecification",
    "EntertainmentBusiness", "EntryPoint", "Enumeration", "Episode", "Event", "EventReservation", "EventStatusType",
    "EventVenue", "ExerciseAction", "ExerciseGym", "ExhibitionEvent", "FAQPage", "FMRadioChannel",
    "FastFoodRestaurant", "Festival", "FilmAction", "FinancialProduct", "FinancialService", "FindAction",
    "FireStation", "Flight", "FlightReservation", "Float", "Florist", "FollowAction", "FoodEstablishment",
    "FoodEstablishmentReservation", "FoodEvent", "FoodService", "FurnitureStore", "Game", "GamePlayMode",
    "GameServer", "GameServerStatus", "GardenStore", "GasStation", "GatedResidenceCommunity", "GenderType",
    "GeneralContractor", "GeoCircle", "GeoCoordinates", "GeoShape", "GiveAction", "GolfCourse",
    "GovernmentBuilding", "GovernmentOffice", "GovernmentOrganization", "GovernmentPermit", "GovernmentService",
    "GroceryStore", "HVACBusiness", "HairSalon", "HardwareStore", "HealthAndBeautyBusiness", "HealthClub",
    "HighSchool", "HinduTemple", "HobbyShop", "HomeAndConstructionBusiness", "HomeGoodsStore", "Hospital", "Hostel",
    "Hotel", "HotelRoom", "House", "HousePainter", "HowTo", "HowToDirection", "HowToItem", "HowToSection",
    "HowToStep", "HowToSupply", "HowToTip", "HowToTool", "IceCreamShop", "IgnoreAction", "ImageGallery",
    "ImageObject", "IndividualProduct", "InformAction", "InsertAction", "InstallAction", "InsuranceAgency",
    "Intangible", "Integer", "InteractAction", "InteractionCounter", "InternetCafe", "InvestmentOrDeposit",
    "InviteAction", "Invoice", "ItemAvailability", "ItemList", "ItemListOrderType", "ItemPage", "JewelryStore",
    "JobPosting", "JoinAction", "LakeBodyOfWater", "Landform", "LandmarksOrHistoricalBuildings", "Language",
    "LeaveAction", "LegalService", "LegislativeBuilding", "LendAction", "Library", "LikeAction", "LiquorStore",
    "ListItem", "ListenAction", "LiteraryEvent", "LiveBlogPosting", "LoanOrCredit", "LocalBusiness",
    "LocationFeatureSpecification", "LockerDelivery", "Locksmith", "LodgingBusiness", "LodgingReservation",
    "LoseAction", "Map", "MapCategoryType", "MarryAction", "Mass", "MediaObject", "MediaSubscription",
    "MedicalOrganization", "MeetingRoom", "MensClothingStore", "Menu", "MenuItem", "MenuSection", "Message",
    "MiddleSchool", "MobileApplication", "MobilePhoneStore", "MonetaryAmount", "MonetaryAmountDistribution",
    "Mosque", "Motel", "MotorcycleDealer", "MotorcycleRepair", "Mountain", "MoveAction", "Movie", "MovieClip",
    "MovieRentalStore", "MovieSeries", "MovieTheater", "MovingCompany", "Museum", "MusicAlbum",
    "MusicAlbumProductionType", "MusicAlbumReleaseType", "MusicComposition", "MusicEvent", "MusicGroup",
    "MusicPlaylist", "MusicRecording", "MusicRelease", "MusicReleaseFormatType", "MusicStore", "MusicVenue",
    "MusicVideoObject", "NGO", "NailSalon", "NewsArticle", "NightClub", "Notary", "NoteDigitalDocument", "Number",
    "NutritionInformation", "Occupation", "OceanBodyOfWater", "Offer", "OfferCatalog", "OfferItemCondition",
    "OfficeEquipmentStore", "OnDemandEvent", "OpeningHoursSpecification", "Order", "OrderAction", "OrderItem",
    "OrderStatus", "Organization", "OrganizationRole", "OrganizeAction", "OutletStore", "OwnershipInfo",
    "PaintAction", "Painting", "ParcelDelivery", "ParcelService", "ParentAudience", "Park", "ParkingFacility",
    "PawnShop", "PayAction", "PaymentCard", "PaymentChargeSpecification", "PaymentMethod", "PaymentService",
    "PaymentStatusType", "PeopleAudience", "PerformAction", "PerformanceRole", "PerformingArtsTheater",
    "PerformingGroup", "Periodical", "Permit", "Person", "PetStore", "Pharmacy", "Photograph", "PhotographAction",
    "Physician", "Place", "PlaceOfWorship", "PlanAction", "PlayAction", "Playground", "Plumber", "PoliceStation",
    "Pond", "PostOffice", "PostalAddress", "PreOrderAction", "PrependAction", "Preschool",
    "PresentationDigitalDocument", "PriceSpecification", "Product", "ProductModel", "ProfessionalService",
    "ProfilePage", "ProgramMembership", "PropertyValue", "PropertyValueSpecification", "PublicSwimmingPool",
    "PublicationEvent", "PublicationIssue", "PublicationVolume", "QAPage", "QualitativeValue", "QuantitativeValue",
    "QuantitativeValueDistribution", "Quantity", "Question", "QuoteAction", "RVPark", "RadioChannel", "RadioClip",
    "RadioEpisode", "RadioSeason", "RadioSeries", "RadioStation", "Rating", "ReactAction", "ReadAction",
    "RealEstateAgent", "ReceiveAction", "Recipe", "RecyclingCenter", "RegisterAction", "RejectAction", "RentAction",
    "RentalCarReservation", "ReplaceAction", "ReplyAction", "Report", "Reservation", "ReservationPackage",
    "ReservationStatusType", "ReserveAction", "Reservoir", "Residence", "Resort", "Restaurant", "RestrictedDiet",
    "ResumeAction", "ReturnAction", "Review", "ReviewAction", "RiverBodyOfWater", "Role", "RoofingContractor",
    "Room", "RsvpAction", "RsvpResponseType", "SaleEvent", "ScheduleAction", "ScholarlyArticle", "School",
    "ScreeningEvent", "Sculpture", "SeaBodyOfWater", "SearchAction", "SearchResultsPage", "Season", "Seat",
    "SelfStorage", "SellAction", "SendAction", "Series", "Service", "ServiceChannel", "ShareAction", "ShoeStore",
    "ShoppingCenter", "SingleFamilyResidence", "SiteNavigationElement", "SkiResort", "SocialEvent",
    "SocialMediaPosting", "SoftwareApplication", "SoftwareSourceCode", "SomeProducts", "SpeakableSpecification",
    "Specialty", "SportingGoodsStore", "SportsActivityLocation", "SportsClub", "SportsEvent", "SportsOrganization",
    "SportsTeam", "SpreadsheetDigitalDocument", "StadiumOrArena", "State", "SteeringPositionValue", "Store",
    "StructuredValue", "SubscribeAction", "SubwayStation", "Suite", "SuspendAction", "Synagogue", "TVClip",
    "TVEpisode", "TVSeason", "TVSeries", "Table", "TakeAction", "TattooParlor", "Taxi", "TaxiReservation",
    "TaxiService", "TaxiStand", "TechArticle", "TelevisionChannel", "TelevisionStation", "TennisComplex", "Text",
    "TextDigitalDocument", "TheaterEvent", "TheaterGroup", "Thing", "Ticket", "TieAction", "Time", "TipAction",
    "TireShop", "TouristAttraction", "TouristInformationCenter", "ToyStore", "TrackAction", "TradeAction",
    "TrainReservation", "TrainStation", "TrainTrip", "TransferAction", "TravelAction", "TravelAgency", "Trip",
    "TypeAndQuantityNode", "URL", "UnRegisterAction", "UnitPriceSpecification", "UpdateAction", "UseAction",
    "UserBlocks", "UserCheckins", "UserComments", "UserDownloads", "UserInteraction", "UserLikes", "UserPageVisits",
    "UserPlays", "UserPlusOnes", "UserTweets", "Vehicle", "VideoGallery", "VideoGame", "VideoGameClip",
    "VideoGameSeries", "VideoObject", "ViewAction", "VisualArtsEvent", "VisualArtwork", "Volcano", "VoteAction",
    "WPAdBlock", "WPFooter", "WPHeader", "WPSideBar", "WantAction", "WarrantyPromise", "WarrantyScope",
    "WatchAction", "Waterfall", "WearAction", "WebApplication", "WebPage", "WebPageElement", "WebSite",
    "WholesaleStore", "WinAction", "Winery", "WorkersUnion", "WriteAction", "Zoo", "ActiveActionStatus",
    "AlbumRelease", "AllWheelDriveConfiguration", "AudiobookFormat", "BroadcastRelease", "CDFormat",
    "CassetteFormat", "CoOp", "CommentPermission", "CompilationAlbum", "CompletedActionStatus", "DJMixAlbum",
    "DVDFormat", "DamagedCondition", "DemoAlbum", "DiabeticDiet", "DigitalAudioTapeFormat", "DigitalFormat",
    "Discontinued", "EBook", "EPRelease", "EventCancelled", "EventPostponed", "EventRescheduled", "EventScheduled",
    "FailedActionStatus", "False", "Female", "FourWheelDriveConfiguration", "Friday",
    "FrontWheelDriveConfiguration", "GlutenFreeDiet", "GroupBoardingPolicy", "HalalDiet", "Hardcover",
    "HearingImpairedSupported", "HinduDiet", "InStock", "InStoreOnly", "ItemListOrderAscending",
    "ItemListOrderDescending", "ItemListUnordered", "KosherDiet", "LaserDiscFormat", "LeftHandDriving",
    "LimitedAvailability", "LiveAlbum", "LowCalorieDiet", "LowFatDiet", "LowLactoseDiet", "LowSaltDiet", "Male",
    "MixtapeAlbum", "Monday", "MultiPlayer", "NewCondition", "OfflinePermanently", "OfflineTemporarily",
    "OnSitePickup", "Online", "OnlineFull", "OnlineOnly", "OrderCancelled", "OrderDelivered", "OrderInTransit",
    "OrderPaymentDue", "OrderPickupAvailable", "OrderProblem", "OrderProcessing", "OrderReturned", "OutOfStock",
    "Paperback", "ParkingMap", "PaymentAutomaticallyApplied", "PaymentComplete", "PaymentDeclined", "PaymentDue",
    "PaymentPastDue", "PotentialActionStatus", "PreOrder", "PreSale", "PublicHolidays", "ReadPermission",
    "RearWheelDriveConfiguration", "RefurbishedCondition", "RemixAlbum", "Researcher", "ReservationCancelled",
    "ReservationConfirmed", "ReservationHold", "ReservationPending", "RightHandDriving", "RsvpResponseMaybe",
    "RsvpResponseNo", "RsvpResponseYes", "Saturday", "SeatingMap", "SinglePlayer", "SingleRelease", "SoldOut",
    "SoundtrackAlbum", "SpokenWordAlbum", "StudioAlbum", "Sunday", "Thursday", "TollFree", "TransitMap", "True",
    "Tuesday", "UsedCondition", "VeganDiet", "VegetarianDiet", "VenueMap", "VinylFormat", "Wednesday",
    "WritePermission", "ZoneBoardingPolicy", "acceptedAnswer", "acceptedOffer", "acceptedPaymentMethod",
    "acceptsReservations", "accessCode", "accessMode", "accessModeSufficient", "accessibilityAPI",
    "accessibilityControl", "accessibilityFeature", "accessibilityHazard", "accessibilitySummary", "accountId",
    "accountablePerson", "acquiredFrom", "actionAccessibilityRequirement", "actionPlatform", "actionStatus",
    "actors", "addOn", "additionalName", "additionalNumberOfGuests", "additionalProperty", "additionalType",
    "address", "addressCountry", "addressLocality", "addressRegion", "advanceBookingRequirement", "affiliation",
    "afterMedia", "agent", "aggregateRating", "aircraft", "albumProductionType", "albumReleaseType", "albums",
    "alignmentType", "alternateName", "alternativeHeadline", "amenityFeature", "amount", "amountOfThisGood",
    "annualPercentageRate", "answerCount", "application", "applicationCategory", "applicationSubCategory",
    "applicationSuite", "appliesToDeliveryMethod", "appliesToPaymentMethod", "area", "arrivalAirport",
    "arrivalBusStop", "arrivalGate", "arrivalPlatform", "arrivalStation", "arrivalTerminal", "arrivalTime",
    "artEdition", "artMedium", "artform", "articleBody", "articleSection", "assembly", "assemblyVersion",
    "associatedArticle", "associatedMedia", "athlete", "attendees", "audienceType", "audio", "authenticator",
    "author", "availability", "availabilityEnds", "availabilityStarts", "availableAtOrFrom", "availableChannel",
    "availableDeliveryMethod", "availableFrom", "availableLanguage", "availableThrough", "awards", "awayTeam",
    "baseSalary", "bccRecipient", "bed", "beforeMedia", "benefits", "bestRating", "billingAddress",
    "billingIncrement", "billingPeriod", "birthDate", "birthPlace", "bitrate", "blogPosts", "boardingGroup",
    "boardingPolicy", "bookEdition", "bookFormat", "bookingAgent", "bookingTime", "borrower", "box", "branchCode",
    "branchOf", "brand", "breadcrumb", "broadcastAffiliateOf", "broadcastChannelId", "broadcastDisplayName",
    "broadcastFrequency", "broadcastFrequencyValue", "broadcastOfEvent", "broadcastServiceTier",
    "broadcastTimezone", "broadcaster", "browserRequirements", "busName", "busNumber", "businessFunction", "buyer",
    "byArtist", "calories", "candidate", "caption", "carbohydrateContent", "cargoVolume", "carrier",
    "carrierRequirements", "catalog", "catalogNumber", "category", "ccRecipient", "character", "characterAttribute",
    "characterName", "cheatCode", "checkinTime", "checkoutTime", "childMaxAge", "childMinAge", "children",
    "cholesterolContent", "circle", "citation", "claimReviewed", "clipNumber", "closes", "coach", "codeRepository",
    "colleagues", "collection", "color", "comment", "commentCount", "commentText", "commentTime", "composer",
    "confirmationNumber", "contactOption", "contactPoints", "contactType", "containedIn", "contentRating",
    "contentSize", "contentType", "contentUrl", "contributor", "cookTime", "cookingMethod", "copyrightHolder",
    "copyrightYear", "countriesNotSupported", "countriesSupported", "countryOfOrigin", "course", "courseCode",
    "courseMode", "coursePrerequisites", "coverageEndTime", "coverageStartTime", "creator", "creditedTo",
    "cssSelector", "currenciesAccepted", "currency", "customer", "dataFeedElement", "datasetTimeInterval",
    "dateCreated", "dateDeleted", "dateIssued", "dateModified", "datePosted", "datePublished", "dateRead",
    "dateReceived", "dateSent", "dateVehicleFirstRegistered", "dateline", "dayOfWeek", "deathDate", "deathPlace",
    "defaultValue", "deliveryAddress", "deliveryLeadTime", "deliveryMethod", "deliveryStatus", "department",
    "departureAirport", "departureBusStop", "departureGate", "departurePlatform", "departureStation",
    "departureTerminal", "departureTime", "dependencies", "depth", "device", "directors",
    "disambiguatingDescription", "discount", "discountCode", "discountCurrency", "discusses", "discussionUrl",
    "dissolutionDate", "distance", "distribution", "doorTime", "downloadUrl", "downvoteCount",
    "driveWheelConfiguration", "dropoffLocation", "dropoffTime", "duns", "durationOfWarranty", "duringMedia",
    "editor", "educationalAlignment", "educationalCredentialAwarded", "educationalFramework", "educationalRole",
    "educationalUse", "elevation", "eligibleCustomerType", "eligibleDuration", "eligibleQuantity", "eligibleRegion",
    "eligibleTransactionVolume", "email", "embedUrl", "employees", "employmentType", "encodingType", "encodings",
    "endDate", "endTime", "endorsee", "entertainmentBusiness", "episodeNumber", "episodes", "equal", "error",
    "estimatedCost", "estimatedFlightDuration", "estimatedSalary", "eventStatus", "events", "exifData",
    "expectedArrivalFrom", "expectedArrivalUntil", "expectsAcceptanceOf", "experienceRequirements", "expires",
    "familyName", "fatContent", "faxNumber", "featureList", "feesAndCommissionsSpecification", "fiberContent",
    "fileFormat", "fileSize", "firstPerformance", "flightDistance", "flightNumber", "floorSize", "followee",
    "follows", "foodEstablishment", "foodEvent", "founders", "foundingDate", "foundingLocation", "free",
    "fromLocation", "fuelConsumption", "fuelEfficiency", "fuelType", "funder", "gameItem", "gameLocation",
    "gamePlatform", "gameTip", "gender", "genre", "geo", "geoContains", "geoCoveredBy", "geoCovers", "geoCrosses",
    "geoDisjoint", "geoEquals", "geoIntersects", "geoMidpoint", "geoOverlaps", "geoRadius", "geoTouches",
    "geoWithin", "geographicArea", "givenName", "globalLocationNumber", "grantee", "greater", "greaterOrEqual",
    "gtin12", "gtin13", "gtin14", "gtin8", "hasCourseInstance", "hasDeliveryMethod", "hasDigitalDocumentPermission",
    "hasMenuItem", "hasMenuSection", "hasOccupation", "hasOfferCatalog", "hasPOS", "headline", "height",
    "highPrice", "hiringOrganization", "homeLocation", "homeTeam", "honorificPrefix", "honorificSuffix",
    "hostingOrganization", "hoursAvailable", "httpMethod", "iataCode", "icaoCode", "illustrator", "inAlbum",
    "inBroadcastLineup", "inPlaylist", "incentives", "includedComposition", "includedDataCatalog", "includesObject",
    "industry", "ineligibleRegion", "ingredients", "installUrl", "instructor", "interactionCount",
    "interactionService", "interactionType", "interactivityType", "interestRate", "inventoryLevel",
    "isAccessoryOrSparePartFor", "isBasedOnUrl", "isConsumableFor", "isFamilyFriendly", "isGift", "isLiveBroadcast",
    "isRelatedTo", "isSimilarTo", "isVariantOf", "isbn", "isicV4", "isrcCode", "issn", "issueNumber", "issuedBy",
    "issuedThrough", "iswcCode", "item", "itemCondition", "itemListElement", "itemListOrder", "itemOffered",
    "itemReviewed", "itemShipped", "jobLocation", "jobTitle", "keywords", "knownVehicleDamages", "knows",
    "landlord", "language", "lastReviewed", "latitude", "learningResourceType", "legalName", "leiCode", "lender",
    "lesser", "lesserOrEqual", "license", "line", "liveBlogUpdate", "loanTerm", "locationCreated",
    "lodgingUnitDescription", "lodgingUnitType", "logo", "longitude", "loser", "lowPrice", "lyricist", "lyrics",
    "mainContentOfPage", "manufacturer", "map", "mapType", "maps", "maxPrice", "maxValue",
    "maximumAttendeeCapacity", "mealService", "median", "members", "membershipNumber", "memoryRequirements",
    "mentions", "menu", "menuAddOn", "merchant", "messageAttachment", "mileageFromOdometer", "minPrice", "minValue",
    "minimumPaymentDue", "model", "modifiedTime", "mpn", "multipleValues", "musicArrangement", "musicBy",
    "musicCompositionForm", "musicGroupMember", "musicReleaseFormat", "musicalKey", "naics", "name",
    "namedPosition", "nationality", "netWorth", "nextItem", "nonEqual", "numAdults", "numChildren", "numTracks",
    "numberOfAirbags", "numberOfAxles", "numberOfBeds", "numberOfDoors", "numberOfEmployees", "numberOfEpisodes",
    "numberOfForwardGears", "numberOfItems", "numberOfPages", "numberOfPlayers", "numberOfPreviousOwners",
    "numberOfRooms", "numberOfSeasons", "numberedPosition", "nutrition", "occupancy", "occupationLocation",
    "occupationalCategory", "offerCount", "offers", "openingHours", "openingHoursSpecification", "opens",
    "operatingSystem", "opponent", "option", "orderDate", "orderDelivery", "orderItemNumber", "orderItemStatus",
    "orderNumber", "orderQuantity", "orderStatus", "orderedItem", "organizer", "originAddress", "ownedFrom",
    "ownedThrough", "owns", "pageEnd", "pageStart", "pagination", "parentItem", "parentService", "parents",
    "partOfEpisode", "partOfInvoice", "partOfOrder", "partOfSeason", "partOfTVSeries", "partySize",
    "passengerPriorityStatus", "passengerSequenceNumber", "paymentAccepted", "paymentDue", "paymentMethod",
    "paymentMethodId", "paymentStatus", "paymentUrl", "percentile10", "percentile25", "percentile75",
    "percentile90", "performerIn", "performers", "permissionType", "permissions", "permitAudience",
    "permittedUsage", "petsAllowed", "photos", "pickupLocation", "pickupTime", "playMode", "playerType",
    "playersOnline", "polygon", "postOfficeBoxNumber", "postalCode", "potentialAction", "predecessorOf", "prepTime",
    "previousItem", "previousStartDate", "price", "priceComponent", "priceCurrency", "priceRange",
    "priceSpecification", "priceType", "priceValidUntil", "primaryImageOfPage", "printColumn", "printEdition",
    "printPage", "printSection", "processingTime", "processorRequirements", "producer", "produces", "productID",
    "productSupported", "productionCompany", "productionDate", "proficiencyLevel", "programMembershipUsed",
    "programName", "programmingLanguage", "programmingModel", "propertyID", "proteinContent", "providerMobility",
    "providesService", "publicAccess", "publication", "publishedOn", "publisher", "publishingPrinciples",
    "purchaseDate", "query", "quest", "question", "ratingCount", "ratingValue", "readonlyValue", "realEstateAgent",
    "recipe", "recipeCategory", "recipeCuisine", "recipeInstructions", "recipeYield", "recordLabel",
    "referenceQuantity", "referencesOrder", "regionsAllowed", "relatedLink", "relatedTo", "releaseDate",
    "releaseNotes", "releasedEvent", "relevantOccupation", "remainingAttendeeCapacity", "replacee", "replacer",
    "replyToUrl", "reportNumber", "representativeOfPage", "requiredCollateral", "requiredGender", "requiredMaxAge",
    "requiredMinAge", "requiredQuantity", "requirements", "requiresSubscription", "reservationFor", "reservationId",
    "reservationStatus", "reservedTicket", "responsibilities", "resultComment", "resultReview", "reviewAspect",
    "reviewBody", "reviewCount", "reviewRating", "reviewedBy", "reviews", "rsvpResponse", "runtime",
    "salaryCurrency", "sameAs", "sampleType", "saturatedFatContent", "scheduledPaymentDate", "scheduledTime",
    "schemaVersion", "screenCount", "screenshot", "seasonNumber", "seasons", "seatNumber", "seatRow", "seatSection",
    "seatingType", "securityScreening", "seeks", "sender", "serverStatus", "servesCuisine", "serviceAudience",
    "serviceLocation", "serviceOperator", "servicePhone", "servicePostalAddress", "serviceSmsNumber", "serviceType",
    "serviceUrl", "servingSize", "sharedContent", "siblings", "significantLinks", "skills", "sku", "slogan",
    "smokingAllowed", "sodiumContent", "softwareAddOn", "softwareHelp", "softwareVersion", "sourceOrganization",
    "spatial", "spatialCoverage", "speakable", "specialCommitments", "specialOpeningHoursSpecification",
    "specialty", "sport", "sportsActivityLocation", "sportsEvent", "sportsTeam", "spouse", "starRating",
    "startDate", "startTime", "steeringPosition", "stepValue", "steps", "storageRequirements", "streetAddress",
    "subEvents", "subReservation", "subtitleLanguage", "successorOf", "sugarContent", "suggestedGender",
    "suggestedMaxAge", "suggestedMinAge", "suitableForDiet", "supportingData", "surface", "target",
    "targetDescription", "targetName", "targetPlatform", "targetProduct", "targetUrl", "taxID", "telephone",
    "temporal", "text", "thumbnail", "thumbnailUrl", "tickerSymbol", "ticketNumber", "ticketToken", "ticketedSeat",
    "timeRequired", "title", "toLocation", "toRecipient", "tool", "totalPaymentDue", "totalPrice", "totalTime",
    "touristType", "trackingNumber", "trackingUrl", "tracks", "trailer", "trainName", "trainNumber",
    "transFatContent", "transcript", "translator", "typeOfBed", "typeOfGood", "typicalAgeRange", "underName",
    "unitCode", "unitText", "unsaturatedFatContent", "uploadDate", "upvoteCount", "url", "urlTemplate",
    "userInteractionCount", "validFor", "validFrom", "validIn", "validThrough", "validUntil", "value",
    "valueAddedTaxIncluded", "valueMaxLength", "valueMinLength", "valueName", "valuePattern", "valueReference",
    "valueRequired", "vatID", "vehicleConfiguration", "vehicleEngine", "vehicleIdentificationNumber",
    "vehicleInteriorColor", "vehicleInteriorType", "vehicleModelDate", "vehicleSeatingCapacity",
    "vehicleTransmission", "vendor", "version", "video", "videoFormat", "videoFrameSize", "videoQuality",
    "volumeNumber", "warrantyPromise", "warrantyScope", "webCheckinTime", "weight", "width", "winner", "wordCount",
    "workHours", "workLocation", "workPerformed", "workPresented", "worksFor", "worstRating", "xpath",
    "yearlyRevenue", "yearsInOperation", "actionApplication", "actionOption", "actor", "album", "albumRelease",
    "alumni", "alumniOf", "artworkSurface", "attendee", "audience", "availableOnDevice", "award", "blogPost",
    "broker", "codeSampleType", "colleague", "contactPoint", "containsPlace", "containsSeason", "contentLocation",
    "dataset", "description", "director", "duration", "employee", "encodesCreativeWork", "encodingFormat",
    "episode", "event", "exampleOfWork", "executableLibraryName", "exerciseCourse", "founder", "game", "gameServer",
    "hasBroadcastChannel", "hasMenu", "inLanguage", "incentiveCompensation", "interactionStatistic",
    "isAccessibleForFree", "isBasedOn", "jobBenefits", "mainEntity", "mainEntityOfPage", "makesOffer", "offeredBy",
    "parent", "partOfSeries", "paymentDueDate", "performTime", "performer", "photo", "provider",
    "providesBroadcastService", "recipeIngredient", "recordedAs", "recordedAt", "recordedIn", "recordingOf",
    "releaseOf", "review", "roleName", "runtimePlatform", "season", "serialNumber", "serviceArea", "serviceOutput",
    "sibling", "significantLink", "softwareRequirements", "sponsor", "subOrganization", "subjectOf",
    "suggestedAnswer", "superEvent", "targetCollection", "temporalCoverage", "track", "warranty", "workExample",
    "yield", "about", "competitor", "containedInPlace", "encoding", "hasMap", "image", "material", "memberOf",
    "parentOrganization", "result", "seller", "step", "subEvent", "supply", "workFeatured", "areaServed",
    "includedInDataCatalog", "member", "recipient", "hasPart", "isPartOf", "position", "instrument", "object",
    "location", "participant", "identifier",
]);
// From https://www.w3.org/ns/shacl.ttl
const sh = Namespace("http://www.w3.org/ns/shacl#", [
    "Shape", "NodeShape", "PropertyShape", "deactivated", "targetClass", "targetNode", "targetObjectsOf",
    "targetSubjectsOf", "message", "severity", "NodeKind", "BlankNode", "BlankNodeOrIRI", "BlankNodeOrLiteral",
    "IRI", "IRIOrLiteral", "Literal", "ValidationReport", "conforms", "result", "shapesGraphWellFormed",
    "AbstractResult", "ValidationResult", "Severity", "Info", "Violation", "Warning", "detail", "focusNode",
    "resultMessage", "resultPath", "resultSeverity", "sourceConstraint", "sourceShape", "sourceConstraintComponent",
    "value", "shapesGraph", "suggestedShapesGraph", "entailment", "path", "inversePath", "alternativePath",
    "zeroOrMorePath", "oneOrMorePath", "zeroOrOnePath", "Parameterizable", "parameter", "labelTemplate",
    "Parameter", "optional", "ConstraintComponent", "validator", "nodeValidator", "propertyValidator", "Validator",
    "SPARQLAskValidator", "SPARQLSelectValidator", "AndConstraintComponent", "AndConstraintComponent-and", "and",
    "ClassConstraintComponent", "ClassConstraintComponent-class", "class", "ClosedConstraintComponent",
    "ClosedConstraintComponent-closed", "ClosedConstraintComponent-ignoredProperties", "closed",
    "ignoredProperties", "DatatypeConstraintComponent", "DatatypeConstraintComponent-datatype", "datatype",
    "DisjointConstraintComponent", "DisjointConstraintComponent-disjoint", "disjoint", "EqualsConstraintComponent",
    "EqualsConstraintComponent-equals", "equals", "HasValueConstraintComponent",
    "HasValueConstraintComponent-hasValue", "hasValue", "InConstraintComponent", "InConstraintComponent-in", "in",
    "LanguageInConstraintComponent", "LanguageInConstraintComponent-languageIn", "languageIn",
    "LessThanConstraintComponent", "LessThanConstraintComponent-lessThan", "lessThan",
    "LessThanOrEqualsConstraintComponent", "LessThanOrEqualsConstraintComponent-lessThanOrEquals",
    "lessThanOrEquals", "MaxCountConstraintComponent", "MaxCountConstraintComponent-maxCount", "maxCount",
    "MaxExclusiveConstraintComponent", "MaxExclusiveConstraintComponent-maxExclusive", "maxExclusive",
    "MaxInclusiveConstraintComponent", "MaxInclusiveConstraintComponent-maxInclusive", "maxInclusive",
    "MaxLengthConstraintComponent", "MaxLengthConstraintComponent-maxLength", "maxLength",
    "MinCountConstraintComponent", "MinCountConstraintComponent-minCount", "minCount",
    "MinExclusiveConstraintComponent", "MinExclusiveConstraintComponent-minExclusive", "minExclusive",
    "MinInclusiveConstraintComponent", "MinInclusiveConstraintComponent-minInclusive", "minInclusive",
    "MinLengthConstraintComponent", "MinLengthConstraintComponent-minLength", "minLength",
    "NodeConstraintComponent", "NodeConstraintComponent-node", "node", "NodeKindConstraintComponent",
    "NodeKindConstraintComponent-nodeKind", "nodeKind", "NotConstraintComponent", "NotConstraintComponent-not",
    "not", "OrConstraintComponent", "OrConstraintComponent-or", "or", "PatternConstraintComponent",
    "PatternConstraintComponent-pattern", "PatternConstraintComponent-flags", "flags", "pattern",
    "PropertyConstraintComponent", "PropertyConstraintComponent-property", "property",
    "QualifiedMaxCountConstraintComponent", "QualifiedMaxCountConstraintComponent-qualifiedMaxCount",
    "QualifiedMaxCountConstraintComponent-qualifiedValueShape",
    "QualifiedMaxCountConstraintComponent-qualifiedValueShapesDisjoint", "QualifiedMinCountConstraintComponent",
    "QualifiedMinCountConstraintComponent-qualifiedMinCount",
    "QualifiedMinCountConstraintComponent-qualifiedValueShape",
    "QualifiedMinCountConstraintComponent-qualifiedValueShapesDisjoint", "qualifiedMaxCount", "qualifiedMinCount",
    "qualifiedValueShape", "qualifiedValueShapesDisjoint", "UniqueLangConstraintComponent",
    "UniqueLangConstraintComponent-uniqueLang", "uniqueLang", "XoneConstraintComponent",
    "XoneConstraintComponent-xone", "xone", "SPARQLExecutable", "SPARQLAskExecutable", "ask",
    "SPARQLConstructExecutable", "construct", "SPARQLSelectExecutable", "select", "SPARQLUpdateExecutable",
    "update", "prefixes", "PrefixDeclaration", "declare", "prefix", "namespace", "SPARQLConstraintComponent",
    "SPARQLConstraintComponent-sparql", "sparql", "SPARQLConstraint", "defaultValue", "description", "group",
    "name", "order", "PropertyGroup", "target", "Target", "TargetType", "SPARQLTarget", "SPARQLTargetType",
    "Function", "returnType", "SPARQLFunction", "resultAnnotation", "ResultAnnotation", "annotationProperty",
    "annotationValue", "annotationVarName", "this", "filterShape", "nodes", "intersection", "union",
    "ExpressionConstraintComponent", "ExpressionConstraintComponent-expression", "expression", "Rule", "rule",
    "condition", "TripleRule", "subject", "predicate", "object", "SPARQLRule", "JSExecutable", "JSTarget",
    "JSTargetType", "JSConstraint", "JSConstraintComponent", "JSConstraint-js", "js", "jsFunctionName", "jsLibrary",
    "jsLibraryURL", "JSFunction", "JSLibrary", "JSRule", "JSValidator",
]);
// From https://www.w3.org/TR/2012/REC-xmlschema11-2-20120405/#sec-datatypes-and-facets with subtractions from
// https://www.w3.org/2011/rdf-wg/wiki/XSD_Datatypes which don't match any mapping
const xsd = Namespace("http://www.w3.org/2001/XMLSchema#", [
    "string", "boolean", "float", "double", "decimal", "dateTime", "time", "date", "gYearMonth", "gYear",
    "gMonthDay", "gDay", "gMonth", "hexBinary", "base64Binary", "anyURI", "normalizedString", "token", "language",
    "NMTOKEN", "Name", "NCName", "integer", "nonPositiveInteger", "negativeInteger", "long", "int", "short", "byte",
    "nonNegativeInteger", "unsignedLong", "unsignedInt", "unsignedShort", "unsignedByte", "positiveInteger",
    "yearMonthDuration", "dayTimeDuration", "dateTimeStamp",
]);
const defaultNS = Object.freeze({
    argu: Namespace("https://argu.co/ns/core#"),
    as,
    bibo: Namespace("http://purl.org/ontology/bibo/"),
    cc: Namespace("http://creativecommons.org/ns#"),
    dbo: Namespace("http://dbpedia.org/ontology/"),
    dbp: Namespace("http://dbpedia.org/property/"),
    dbpedia: Namespace("http://dbpedia.org/resource/"),
    dc,
    dcat: Namespace("http://www.w3.org/ns/dcat#"),
    dctype: Namespace("http://purl.org/dc/dcmitype/"),
    ex: Namespace("http://example.com/ns#"),
    example: Namespace("http://www.example.com/"),
    fhir: Namespace("http://hl7.org/fhir/"),
    fhir3: Namespace("http://hl7.org/fhir/STU3"),
    foaf: Namespace("http://xmlns.com/foaf/0.1/"),
    geo: Namespace("http://www.w3.org/2003/01/geo/wgs84_pos#"),
    http: Namespace("http://www.w3.org/2011/http#"),
    http07: Namespace("http://www.w3.org/2007/ont/http#"),
    httph: Namespace("http://www.w3.org/2007/ont/httph#"),
    hydra: Namespace("http://www.w3.org/ns/hydra/core#"),
    ianalr: Namespace("http://www.iana.org/assignments/link-relations/"),
    link: Namespace("http://www.w3.org/2007/ont/link#"),
    ll: Namespace("http://purl.org/link-lib/"),
    owl: Namespace("http://www.w3.org/2002/07/owl#"),
    p: Namespace("http://www.wikidata.org/prop/"),
    prov: Namespace("http://www.w3.org/ns/prov#"),
    qb: Namespace("http://purl.org/linked-data/cube#"),
    rdf,
    rdfs,
    schema,
    sh,
    skos: Namespace("http://www.w3.org/2004/02/skos/core#"),
    wd: Namespace("http://www.wikidata.org/entity/"),
    wdata: Namespace("https://www.wikidata.org/wiki/Special:EntityData/"),
    wdref: Namespace("http://www.wikidata.org/reference/"),
    wds: Namespace("http://www.wikidata.org/entity/statement/"),
    wdt: Namespace("http://www.wikidata.org/prop/direct/"),
    wdv: Namespace("http://www.wikidata.org/value/"),
    xmlns: Namespace("http://www.w3.org/2000/xmlns/"),
    xsd,
});
const DEFAULT_TOPOLOGY = defaultNS.ll("defaultTopology");
/** Constant used to determine that a class is used to render a type rather than a property. */
const RENDER_CLASS_NAME = defaultNS.ll("typeRenderClass");
const MAIN_NODE_DEFAULT_IRI = defaultNS.ll("targetResource");
// tslint:disable-next-line ban-types
const NON_DATA_OBJECTS_CTORS = [
    Array,
    ArrayBuffer,
    Boolean,
    DataView,
    Date,
    Error,
    EvalError,
    Float32Array,
    Float64Array,
    Int16Array,
    Int32Array,
    Int8Array,
    Intl.Collator,
    Intl.DateTimeFormat,
    Intl.NumberFormat,
    Map,
    Number,
    Promise,
    (typeof Proxy !== "undefined" ? Proxy : undefined),
    RangeError,
    ReferenceError,
    RegExp,
    Set,
].filter(Boolean);
const MSG_BAD_REQUEST = "Request failed with bad status code";
const MSG_INCORRECT_TARGET = "Collections or Literals can't be the target";
const MSG_URL_UNDEFINED = "No url given with action.";
const MSG_URL_UNRESOLVABLE = "Can't execute action with non-named-node url.";
const MSG_OBJECT_NOT_IRI = "Action object property must be an IRI.";

const MSG_TYPE_ERR = "Non-optimized NamedNode instance given. Please memoize your namespace correctly.";
/** Constant used to determine that a component is used to render a type rather than a property. */
const RENDER_CLASS_NAME$1 = defaultNS.ll("typeRenderClass");
function convertToCacheKey(types, props, topology) {
    return `${types.join()}[${props.join()}][${topology}]`;
}
/**
 * Handles registration and querying for view components.
 */
class ComponentStore {
    constructor(schema) {
        this.lookupCache = {};
        /**
         * Lookup map ordered with the following hierarchy;
         * [propertyType][resourceType][topology]
         */
        this.mapping = [];
        this.schema = schema;
        this.mapping[RENDER_CLASS_NAME$1.sI] = [];
    }
    /**
     * Generate registration description objects for later registration use.
     * @see LinkedRenderStore#registerAll
     */
    static registerRenderer(component, types, properties, topologies) {
        if (typeof component === "undefined") {
            throw new Error(`Undefined component was given for (${types}, ${properties}, ${topologies}).`);
        }
        const registrations = [];
        for (let t = 0; t < types.length; t++) {
            if (types[t] === undefined) {
                throw new TypeError(MSG_TYPE_ERR);
            }
            for (let p = 0; p < properties.length; p++) {
                if (properties[p] === undefined) {
                    throw new TypeError(MSG_TYPE_ERR);
                }
                for (let top = 0; top < topologies.length; top++) {
                    if (topologies[top] === undefined) {
                        throw new TypeError(MSG_TYPE_ERR);
                    }
                    registrations.push({
                        component,
                        property: properties[p],
                        topology: topologies[top],
                        type: types[t],
                    });
                }
            }
        }
        return registrations;
    }
    /**
     * TODO: remove defaultType - Basically a bug. We default the type if no matches were found, rather than using
     *   inheritance to associate unknown types the RDF way (using rdfs:Resource).
     */
    getRenderComponent(types, predicates, topology, defaultType) {
        const oTypes = this.schema.expand(types);
        const key = convertToCacheKey(oTypes, predicates, topology);
        const cached = this.getComponentFromCache(key);
        if (cached !== undefined) {
            return cached;
        }
        for (let i = 0; i < oTypes.length; i++) {
            const exact = this.lookup(predicates[0], oTypes[i], topology);
            if (exact !== undefined) {
                return this.addComponentToCache(exact, key);
            }
        }
        const possibleComponents = this.possibleComponents(predicates, topology);
        if (possibleComponents.length === 0) {
            if (topology === DEFAULT_TOPOLOGY.sI) {
                return undefined;
            }
            const foundComponent = this.getRenderComponent(oTypes, predicates, DEFAULT_TOPOLOGY.sI, defaultType);
            if (!foundComponent) {
                return undefined;
            }
            return this.addComponentToCache(foundComponent, key);
        }
        for (let i = 0; i < predicates.length; i++) {
            const bestComponent = this.bestComponent(possibleComponents, oTypes);
            const component = bestComponent && this.lookup(predicates[i], bestComponent, topology);
            if (component) {
                return this.addComponentToCache(component, key);
            }
        }
        for (let i = 0; i < predicates.length; i++) {
            const component = this.lookup(predicates[i], defaultType, topology);
            if (component) {
                return this.addComponentToCache(component, key);
            }
        }
        return undefined;
    }
    /**
     * Register a renderer for a type/property.
     * @param component The component to return for the rendering of the object.
     * @param type The type's SomeNode of the object which the {component} can render.
     * @param [property] The property's SomeNode if the {component} is a subject renderer.
     * @param [topology] An alternate topology this {component} should render.
     */
    registerRenderer(component, type, property = RENDER_CLASS_NAME$1.sI, topology = DEFAULT_TOPOLOGY.sI) {
        if (!property || !type) {
            return;
        }
        this.store(component, property, type, topology);
        this.lookupCache = {};
    }
    /**
     * Find a component from a cache.
     * @param predicate The index of the property (or {RENDER_CLASS_NAME})
     * @param obj The index of either the resource type or resource IRI
     * @param topology The index of the topology
     * @param cache The cache to look into (defaults to the mapping)
     * @returns The appropriate component if any
     */
    lookup(predicate, obj, topology, cache = this.mapping) {
        const predMap = cache[predicate];
        if (!predMap || !predMap[obj]) {
            return undefined;
        }
        return predMap[obj][topology];
    }
    /** Store a component to a cache. */
    store(component, predicate, obj, topology, cache = this.mapping) {
        if (typeof cache[predicate] === "undefined") {
            cache[predicate] = [];
        }
        if (typeof cache[predicate][obj] === "undefined") {
            cache[predicate][obj] = [];
        }
        cache[predicate][obj][topology] = component;
    }
    /**
     * Adds a renderer to {this.lookupCache}
     * @param component The render component.
     * @param key The memoization key.
     * @returns The renderer passed with {component}
     */
    addComponentToCache(component, key) {
        this.lookupCache[key] = component;
        return this.lookupCache[key];
    }
    /**
     * Expands the given types and returns the best component to render it with.
     * @param components The set of components to choose from.
     * @param [types] The types to expand on.
     * @returns The best match for the given components and types.
     */
    bestComponent(components, types) {
        if (types.length > 0) {
            const direct = this.schema.sort(types).find((c) => components.indexOf(c) >= 0);
            if (direct) {
                return direct;
            }
        }
        const chain = this.schema.expand(types || []);
        return components.find((c) => chain.indexOf(c) > 0);
    }
    /**
     * Resolves a renderer from the {lookupCache}.
     * @param key The key to look up.
     * @returns If saved the render component, otherwise undefined.
     */
    getComponentFromCache(key) {
        return this.lookupCache[key];
    }
    possibleComponents(predicates, topology) {
        const classes = [defaultNS.rdfs("Resource").sI];
        for (let i = 0; i < predicates.length; i++) {
            const predicate = predicates[i];
            if (typeof this.mapping[predicate] !== "undefined") {
                const types = this.mapping[predicate];
                for (let j = 0; j < types.length; j++) {
                    const compType = this.lookup(predicate, j, topology);
                    if (compType !== undefined) {
                        classes.push(j);
                    }
                }
            }
        }
        return classes;
    }
}

/**
 * Tries to schedule updates async if possible.
 */
class ProcessBroadcast {
    constructor(opts) {
        this.hasIdleCallback = "requestIdleCallback" in window;
        this.hasRequestAnimationFrame = "requestAnimationFrame" in window;
        this.resolve = () => undefined;
        this.timeout = opts.timeout;
        this.regUpdateTime = Date.now();
        this.bulkSubscriptions = opts.bulkSubscriptions;
        this.changedSubjects = opts.changedSubjects;
        this.subjectSubscriptions = opts.subjectSubscriptions;
        this.work = Object.freeze(opts.work);
        this.queue = this.queue.bind(this);
    }
    done() {
        return this.work.length === 0
            || this.subjectSubscriptions.length === 0 && this.bulkSubscriptions.length === 0;
    }
    run() {
        if (this.timeout === 0) {
            this.queue();
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.resolve = resolve;
            this.queue();
        });
    }
    /**
     * Calls the subscriber callback function {reg} with the correct arguments according to its
     * registration settings.
     */
    callSubscriber(reg) {
        if (reg.markedForDelete) {
            return;
        }
        reg.callback(reg.onlySubjects ? this.changedSubjects : this.work, this.regUpdateTime);
    }
    process() {
        if (this.bulkSubscriptions.length > 0) {
            this.callSubscriber(this.bulkSubscriptions.pop());
        }
        else if (this.subjectSubscriptions.length > 0) {
            this.callSubscriber(this.subjectSubscriptions.pop());
        }
    }
    queue(idleCallback) {
        if (this.timeout !== 0 && this.hasIdleCallback) {
            while (typeof idleCallback === "object"
                && (!this.done() && (idleCallback.timeRemaining() > 0 || idleCallback.didTimeout))) {
                this.process();
            }
            if (this.done()) {
                return this.resolve();
            }
            window.requestIdleCallback(this.queue, { timeout: this.timeout });
        }
        else if (this.timeout !== 0 && this.hasRequestAnimationFrame) {
            this.process();
            while (typeof idleCallback === "number" && (performance.now() - idleCallback) < 33) {
                this.process();
            }
            if (this.done()) {
                return this.resolve();
            }
            window.requestAnimationFrame(this.queue);
        }
        else {
            while (!this.done()) {
                this.process();
            }
            this.resolve();
        }
    }
}

/* global chrome */
const memberPrefix = defaultNS.rdf("_").value;
/**
 * Filters {obj} to only include statements where the subject equals {predicate}.
 * @param obj The statements to filter.
 * @param predicate The subject to filter for.
 * @return A possibly empty filtered array of statements.
 */
function allRDFPropertyStatements(obj, predicate) {
    if (typeof obj === "undefined") {
        return [];
    }
    if (predicate === defaultNS.rdfs("member")) {
        return obj.filter((s) => s.predicate.value.startsWith(memberPrefix));
    }
    return obj.filter((s) => s.predicate.equals(predicate));
}
/**
 * Filters {obj} on subject {predicate} returning the resulting statements' objects.
 * @see allRDFPropertyStatements
 */
function allRDFValues(obj, predicate) {
    const props = allRDFPropertyStatements(obj, predicate);
    if (props.length === 0) {
        return [];
    }
    return props.map((s) => s.object);
}
/**
 * Resolve {predicate} to any value, if any. If present, additional values are ignored.
 */
function anyRDFValue(obj, predicate) {
    if (!Array.isArray(obj)) {
        return undefined;
    }
    const match = predicate === defaultNS.rdfs("member")
        ? obj.find((s) => s.predicate.value.startsWith(memberPrefix))
        : obj.find((s) => s.predicate.equals(predicate));
    if (typeof match === "undefined") {
        return undefined;
    }
    return match.object;
}
function getPropBestLang(rawProp, langPrefs) {
    if (!Array.isArray(rawProp)) {
        return rawProp.object;
    }
    if (rawProp.length === 1) {
        return rawProp[0].object;
    }
    for (let i = 0; i < langPrefs.length; i++) {
        const pIndex = rawProp.findIndex((p) => "language" in p.object && p.object.language === langPrefs[i]);
        if (pIndex >= 0) {
            return rawProp[pIndex].object;
        }
    }
    return rawProp[0].object;
}
function getPropBestLangRaw(statements, langPrefs) {
    if (!Array.isArray(statements)) {
        return statements;
    }
    if (statements.length === 1) {
        return statements[0];
    }
    for (let i = 0; i < langPrefs.length; i++) {
        const pIndex = statements.findIndex((s) => "language" in s.object && s.object.language === langPrefs[i]);
        if (pIndex >= 0) {
            return statements[pIndex];
        }
    }
    return statements[0];
}
function getTermBestLang(rawTerm, langPrefs) {
    if (!Array.isArray(rawTerm)) {
        return rawTerm;
    }
    if (rawTerm.length === 1) {
        return rawTerm[0];
    }
    for (let i = 0; i < langPrefs.length; i++) {
        const pIndex = rawTerm.findIndex((p) => "language" in p && p.language === langPrefs[i]);
        if (pIndex >= 0) {
            return rawTerm[pIndex];
        }
    }
    return rawTerm[0];
}
/**
 * Checks if the origin of {href} matches current origin from {window.location}
 * @returns `true` if matches, `false` otherwise.
 */
function isDifferentOrigin(href) {
    if (href instanceof BlankNode) {
        return false;
    }
    const origin = href instanceof NamedNode ? href.value : href;
    return !origin.startsWith(self.location.origin + "/");
}
function normalizeType(type) {
    return Array.isArray(type) ? type : [type];
}

/**
 * Fix rdflib issue where multiline strings are serialized in nquads.
 * @see https://github.com/linkeddata/rdflib.js/pull/282
 * @monkey
 */
function patchRDFLibSerializer(serializer, fallback) {
    const old = serializer.stringToN3;
    serializer.stringToN3 = function stringToN3(str, flags) {
        let flagsWithFallback = flags;
        if (!flags) {
            flagsWithFallback = fallback;
        }
        return old(str, flagsWithFallback);
    };
}
/**
 * Patch rdflib with memoized versions of terms by overriding certain object methods.
 * For browsers that don't support Proxy.
 */
function patchRDFLibStoreWithOverrides(graph, changeBufferTarget) {
    // Don't try this at home, kids!
    graph.statements.push = (...elems) => {
        let elem;
        for (let i = 0, len = elems.length; i < len; i++) {
            elem = elems[i];
            changeBufferTarget.changeBuffer[changeBufferTarget.changeBufferCount] = elem;
            changeBufferTarget.changeBufferCount++;
        }
        return Array.prototype.push.call(graph.statements, ...elems);
    };
    graph.statements.splice = (index, len) => {
        const rem = Array.prototype.splice.call(graph.statements, index, len);
        changeBufferTarget.changeBuffer.push(...rem);
        changeBufferTarget.changeBufferCount += len;
        return rem;
    };
    return graph;
}

/**
 * Extracts the content type from a request.
 * The content-type value has precedence if it contains a known type.
 * Otherwise it returns the extension if present, or the content-type without the encoding.
 *
 * @summary Extracts the content type from a request.
 */
function getContentType(res) {
    const contentTypeRaw = getHeader(res, "Content-Type");
    if (contentTypeRaw === undefined || contentTypeRaw === null) {
        return "";
    }
    const contentType = contentTypeRaw.split(";")[0];
    const url = getURL(res);
    const urlMatch = url && new URL(url).href.match(/\.([a-zA-Z0-9]{1,8})($|\?|#)/);
    const ext = urlMatch ? urlMatch[1] : "";
    if (contentType) {
        const matched = contentTypeByMimeString(contentType, ext);
        if (matched) {
            return matched;
        }
    }
    if (ext && !NON_CONTENT_EXTS.includes(ext)) {
        return contentTypeByExtention(ext);
    }
    return contentTypeRaw.split(";")[0];
}
async function getJSON(res) {
    if (res instanceof Response) {
        return res.json();
    }
    else if (res instanceof XMLHttpRequest) {
        return JSON.parse(res.responseText);
    }
    return JSON.parse(res.body);
}
function getHeader(res, header) {
    if (res instanceof Response) {
        return res.headers.get(header);
    }
    else if (typeof XMLHttpRequest !== "undefined" && res instanceof XMLHttpRequest) {
        return res.getResponseHeader(header) || null;
    }
    else if (res && res.headers) {
        const headerValue = res.headers[header];
        return headerValue || null;
    }
    return null;
}
function getURL(res) {
    if (typeof XMLHttpRequest !== "undefined" && res instanceof XMLHttpRequest) {
        return res.responseURL;
    }
    else if ("requestedURI" in res) {
        return res.requestedURI;
    }
    return res.url;
}
function contentTypeByExtention(ext) {
    if (["ttl"].includes(ext)) {
        return F_TURTLE;
    }
    else if (["ntriples", "nt"].includes(ext)) {
        return F_NTRIPLES;
    }
    else if (["jsonld"].includes(ext)) {
        return F_JSONLD;
    }
    else if (["n3"].includes(ext)) {
        return F_N3;
    }
    return ext;
}
function contentTypeByMimeString(contentType, ext) {
    if (contentType.includes(F_NTRIPLES) || contentType.includes(F_NTRIPLES_DEP)) {
        return F_NTRIPLES;
    }
    else if (contentType.includes(F_PLAIN) && ["ntriples", "nt"].indexOf(ext) >= 0) {
        return F_NTRIPLES;
    }
    else if (contentType.includes(F_TURTLE) || contentType.includes(F_TURTLE_DEP)) {
        return F_TURTLE;
    }
    else if (contentType.includes(F_N3)) {
        return F_N3;
    }
    else if (contentType.includes(F_JSONLD)) {
        return F_JSONLD;
    }
    else if (contentType.includes(F_RDF_XML)) {
        return F_RDF_XML;
    }
    return undefined;
}

class ProcessorError extends Error {
    constructor(msg, response) {
        super(msg);
        this.response = response;
    }
}

class RequestInitGenerator {
    constructor(opts = {
        credentials: "include",
        csrfFieldName: "csrf-token",
        mode: "same-origin",
        xRequestedWith: "XMLHttpRequest",
    }) {
        this.csrfFieldName = opts.csrfFieldName;
        this.credentials = opts.credentials;
        this.mode = opts.mode;
        this.xRequestedWith = opts.xRequestedWith;
    }
    authenticityHeader(options = {}) {
        return Object.assign({}, options, {
            "X-CSRF-Token": this.getAuthenticityToken(),
            "X-Requested-With": this.xRequestedWith,
        });
    }
    generate(method = "GET", accept = "text/turtle", body) {
        const isFormEncoded = body instanceof URLSearchParams;
        const headers = this.authenticityHeader({ Accept: accept });
        if (isFormEncoded) {
            headers["Content-Type"] = "application/x-www-form-urlencoded; charset=UTF-8";
        }
        return {
            body: isFormEncoded ? body.toString() : body,
            credentials: "include",
            headers,
            method: method.toUpperCase(),
            mode: "same-origin",
        };
    }
    getMetaContent(name) {
        const header = document.head && document.head.querySelector(`meta[name="${name}"]`);
        return header && header.getAttribute("content");
    }
    getAuthenticityToken() {
        return this.getMetaContent(this.csrfFieldName) || "";
    }
}

const SAFE_METHODS = ["GET", "HEAD", "OPTIONS", "CONNECT", "TRACE"];
const FETCHER_CALLBACKS = ["done", "fail", "refresh", "request", "retract"];
async function handleStatus(res) {
    if (res.status === NOT_FOUND) {
        throw {
            message: `404: '${getURL(res)}' could not be found`,
            res,
        };
    }
    else if (res.status >= BAD_REQUEST && res.status < INTERNAL_SERVER_ERROR) {
        if ((getHeader(res, "Content-Type") || "").includes("json")) {
            const body = await getJSON(res);
            throw {
                message: "errors" in body ? body.errors && body.errors[0] && body.errors[0].message : "",
                res,
            };
        }
        throw {
            message: `404: '${getURL(res)}' could not be found`,
            res,
        };
    }
    else if (res.status >= INTERNAL_SERVER_ERROR) {
        return Promise.reject({
            message: "Internal server error",
            res,
        });
    }
    return res;
}
/**
 * Pushes in-place value {v} onto an array under key {k} of Map {map}.
 * @param map The reference to the Map to add the data to.
 * @param k The key on {map}. An array is initialized when it doesn't yet exists.
 * @param v The value to push on the array under {k}.
 */
function pushToMap(map, k, v) {
    if (typeof map[k] === "undefined") {
        map[k] = [];
    }
    map[k].push(v);
}
/**
 * Saves response metadata into a graph.
 * @param iri The original iri that was fetched.
 * @param res The (fetch) response object from the request.
 * @returns A graph with metadata about the response.
 */
function processResponse(iri, res) {
    const rawURL = getURL(res);
    const origin = iri instanceof NamedNode ? iri.site() : NamedNode.find(new URL(rawURL).origin);
    if (rawURL && iri !== rawURL) {
        return [
            new Statement(NamedNode.find(iri), NamedNode.find("http://www.w3.org/2002/07/owl#sameAs"), NamedNode.find(rawURL), origin),
        ];
    }
    return [];
}
const emptyRequest = Object.freeze({
    lastRequested: null,
    requested: false,
    status: null,
    timesRequested: 0,
});
/**
 * The client (User Agent) has closed the connection, e.g. due to CORS or going offline.
 */
const failedRequest = () => Object.freeze({
    lastRequested: new Date(),
    requested: true,
    status: 499,
    timesRequested: 0,
});
const timedOutRequest = (totalRequested) => Object.freeze({
    lastRequested: new Date(),
    requested: true,
    status: 408,
    timesRequested: totalRequested,
});
const queuedDeltaStatus = (totalRequested) => Object.freeze({
    lastRequested: new Date(),
    requested: true,
    status: NON_AUTHORITATIVE_INFORMATION,
    timesRequested: totalRequested,
});
class DataProcessor {
    constructor(opts) {
        this.timeout = 30000;
        this.deltas = [];
        this.accept = opts.accept || {
            default: "",
        };
        this.bulkEndpoint = `${typeof window !== "undefined" ? window.location.origin : ""}/link-lib/bulk`;
        this._dispatch = opts.dispatch;
        this.invalidationMap = new Map();
        this.requestInitGenerator = opts.requestInitGenerator || new RequestInitGenerator();
        this.mapping = opts.mapping || {};
        this.report = opts.report;
        this.requestMap = new Map();
        this.statusMap = [];
        this.store = opts.store;
        if (opts.fetcher) {
            this.fetcher = opts.fetcher;
        }
        this.execExecHeader = this.execExecHeader.bind(this);
        this.processExecAction = this.processExecAction.bind(this);
        this.feedResponse = this.feedResponse.bind(this);
    }
    get fetcher() {
        if (typeof this._fetcher === "undefined") {
            this._fetcher = new Fetcher(this.store.getInternalStore(), {
                // fetch: typeof window !== "undefined" && window.fetch.bind(window) || undefined,
                timeout: this.timeout,
            });
            FETCHER_CALLBACKS.forEach((hook) => {
                const hookIRI = defaultNS.ll(`data/rdflib/${hook}`);
                this._fetcher.addCallback(hook, this.invalidateStatus.bind(this));
                this._fetcher.addCallback(hook, (iri, _err) => {
                    this.dispatch(hookIRI, [typeof iri === "string" ? NamedNode.find(iri) : iri, _err]);
                    return true;
                });
            });
        }
        return this._fetcher;
    }
    set fetcher(v) {
        this._fetcher = v;
    }
    get dispatch() {
        if (typeof this._dispatch === "undefined") {
            throw new Error("Invariant: cannot call `dispatch` before initialization is complete");
        }
        return this._dispatch;
    }
    set dispatch(value) {
        this._dispatch = value;
    }
    async execActionByIRI(subject, dataTuple) {
        const [graph, blobs = []] = dataTuple;
        await (this.store.statementsFor(subject).length > 0
            ? Promise.resolve([])
            : this.getEntity(subject));
        const object = this.store.getResourceProperty(subject, defaultNS.schema("object"));
        if (!object || object.termType !== "BlankNode" && object.termType !== "NamedNode") {
            throw new ProcessorError(MSG_OBJECT_NOT_IRI);
        }
        const target = this.store.getResourceProperty(subject, defaultNS.schema("target"));
        if (!target || target.termType === "Collection" || target.termType === "Literal") {
            throw new ProcessorError(MSG_INCORRECT_TARGET);
        }
        const urls = this.store.getResourceProperty(target, defaultNS.schema("url"));
        const url = Array.isArray(urls) ? urls[0] : urls;
        if (!url) {
            throw new ProcessorError(MSG_URL_UNDEFINED);
        }
        if (url.termType !== "NamedNode") {
            throw new ProcessorError(MSG_URL_UNRESOLVABLE);
        }
        const targetMethod = this.store.getResourceProperty(target, defaultNS.schema("httpMethod"));
        const method = typeof targetMethod !== "undefined" ? targetMethod.toString() : "GET";
        const opts = this.requestInitGenerator.generate(method, this.acceptForHost(url));
        if (opts.headers instanceof Headers) {
            opts.headers.set("Request-Referrer", subject.value);
        }
        else if (opts.headers && !Array.isArray(opts.headers)) {
            opts.headers["Request-Referrer"] = subject.value;
        }
        if (!SAFE_METHODS.includes(method) && graph && graph !== null && graph.length > 0) {
            if (opts.headers instanceof Headers) {
                opts.headers.delete("Content-Type");
            }
            else if (opts.headers && !Array.isArray(opts.headers)) {
                delete opts.headers["Content-Type"];
            }
            const data = new FormData();
            const s = new Serializer(new IndexedFormula());
            patchRDFLibSerializer(s, "deinprstux");
            s.setFlags("deinprstux");
            const rdfSerialization = s.statementsToNTriples(graph.statements);
            data.append(defaultNS.ll("graph").toString(), new Blob([rdfSerialization], { type: F_NTRIPLES }));
            for (let i = 0; i < blobs.length; i++) {
                data.append(blobs[i][0].toString(), blobs[i][1]);
            }
            opts.body = data;
        }
        const resp = await fetch(url.value, opts).then(this.processExecAction);
        if (resp.status > BAD_REQUEST) {
            // TODO: process responses with a correct content-type.
            throw new ProcessorError(MSG_BAD_REQUEST, resp);
        }
        const statements = await this.feedResponse(resp, true);
        const location = getHeader(resp, "Location");
        const fqLocation = location && uri.join(location, window.location.origin);
        const iri = fqLocation && NamedNode.find(fqLocation) || null;
        return {
            data: statements,
            iri,
        };
    }
    fetchableURLFromIRI(iri) {
        return NamedNode.find(iri.value.split("#").shift());
    }
    async fetchResource(iri, opts) {
        const accept = this.acceptForHost(iri);
        if (accept) {
            this.fetcher.mediatypes = { [accept]: { q: 1.0 } };
        }
        const options = Object.assign({}, {
            credentials: "same-origin",
            headers: {
                "Accept": accept,
                "Content-Type": "application/n-quads",
            },
        }, opts);
        let res;
        try {
            res = await this.fetcher.load(iri, options);
        }
        catch (e) {
            // if (typeof e.response !== "undefined") {
            //     res =  e.response;
            // } else {
            throw e;
            // }
        }
        return this.processExecAction(res);
    }
    flush() {
        const deltas = this.deltas;
        this.deltas = [];
        for (let i = 0; i < deltas.length; i++) {
            try {
                this.processDelta(deltas[i]);
            }
            catch (e) {
                this.report(e);
            }
        }
        return [];
    }
    getEntities(resources) {
        const reload = [];
        const body = new URLSearchParams();
        for (let i = 0; i < resources.length; i++) {
            const resource = resources[i];
            if (resource[1] && resource[1].reload) {
                reload.push(resource[0]);
            }
            body.append("resource[]", encodeURIComponent(resource[0].value));
        }
        const opts = this.requestInitGenerator.generate("POST", this.acceptForHost(this.bulkEndpoint), body);
        return fetch(this.bulkEndpoint, opts)
            .then(this.feedResponse)
            .catch((err) => {
            const status = Literal.fromNumber(err instanceof Error ? 499 : err.status);
            const delta = resources
                .map(([s]) => [s, defaultNS.http("statusCode"), status, defaultNS.ll("meta")]);
            return this.processDelta(delta);
        });
    }
    /**
     * @see LinkedDataAPI#getEntity
     * @param iri The SomeNode of the entity
     * @param opts The options for fetch-/processing the resource.
     * @param opts The options for fetch-/processing the resource.
     * @return A promise with the resulting entity
     */
    async getEntity(iri, opts) {
        const url = new URL(iri.value);
        url.hash = "";
        const requestIRI = NamedNode.find(url.toString());
        const existing = this.requestMap.get(requestIRI.sI);
        if (existing) {
            return existing;
        }
        let preExistingData = [];
        if (opts && opts.clearPreviousData) {
            preExistingData = this.store.statementsFor(iri);
            preExistingData = preExistingData.concat(this.store.statementsFor(requestIRI));
        }
        try {
            const req = this
                .fetchResource(requestIRI, opts)
                .then((res) => {
                this.removeInvalidation(iri);
                this.removeInvalidation(requestIRI);
                this.store.removeStatements(preExistingData);
                return this.feedResponse(res);
            }); // TODO: feedResponse might only be necessary for non-rdflib fetcher requests.
            this.requestMap.set(requestIRI.sI, req);
            return await req;
        }
        catch (e) {
            this.invalidateStatus(iri.sI);
            const response = e.res || e.response;
            if (typeof response === "undefined") {
                throw e;
            }
            this.store.removeStatements(preExistingData);
            const responseQuads = processResponse(iri, response);
            this.store.addStatements(responseQuads);
            return responseQuads;
        }
        finally {
            this.requestMap.delete(requestIRI.sI);
        }
    }
    /**
     * @see LinkedDataAPI#getStatus for documentation
     */
    getStatus(iri) {
        const irl = this.fetchableURLFromIRI(iri);
        const existing = this.statusMap[irl.sI];
        if (existing) {
            return existing;
        }
        const fetcherStatus = this.fetcher.requested[irl.value];
        if (fetcherStatus === undefined) {
            if (irl.value in this.fetcher.requested) {
                return this.memoizeStatus(irl, failedRequest());
            }
            return this.memoizeStatus(irl, emptyRequest);
        }
        const requests = this.store.match(null, defaultNS.link("requestedURI"), Literal.find(irl.value));
        const totalRequested = requests.length;
        if (requests.length === 0) {
            return this.memoizeStatus(irl, emptyRequest);
        }
        if (fetcherStatus === true) {
            return this.memoizeStatus(irl, {
                lastRequested: new Date(),
                lastResponseHeaders: null,
                requested: true,
                status: 202,
                timesRequested: totalRequested,
            });
        }
        if (fetcherStatus === "timeout") {
            return this.memoizeStatus(irl, timedOutRequest(totalRequested));
        }
        const requestIRI = requests.pop().subject;
        const requestObj = anyRDFValue(this.store.statementsFor(requestIRI), defaultNS.link("response"));
        if (!requestObj) {
            return this.memoizeStatus(irl, emptyRequest);
        }
        const requestObjData = this.store.statementsFor(requestObj);
        // RDFLib has different behaviour across browsers and code-paths, so we must check for multiple properties.
        const requestStatus = anyRDFValue(requestObjData, defaultNS.http("status"))
            || anyRDFValue(requestObjData, defaultNS.http07("status"))
            || anyRDFValue(requestObjData, defaultNS.httph("status"));
        const requestDate = anyRDFValue(requestObjData, defaultNS.httph("date"));
        if (!requestStatus) {
            if (fetcherStatus === "done") {
                return this.memoizeStatus(irl, timedOutRequest(totalRequested));
            }
            return this.memoizeStatus(irl, emptyRequest);
        }
        return this.memoizeStatus(irl, {
            lastRequested: requestDate ? new Date(requestDate.value) : new Date(0),
            lastResponseHeaders: requestObj,
            requested: true,
            status: Number.parseInt(requestStatus.value, 10),
            timesRequested: totalRequested,
        });
    }
    invalidateStatus(iri) {
        const id = typeof iri === "number" ? iri : (typeof iri === "string" ? NamedNode.find(iri) : iri).sI;
        // TODO: Don't just remove, but rather mark it as invalidated so it's history isn't lost.
        this.statusMap[id] = undefined;
        return true;
    }
    invalidate(iri, _err) {
        const id = (typeof iri === "string" ? NamedNode.find(iri) : iri).sI;
        this.invalidationMap.set(id);
        this.invalidateStatus(id);
        return true;
    }
    isInvalid(iri) {
        return this.invalidationMap.has(iri.sI);
    }
    processExternalResponse(response) {
        return handleStatus(response)
            .then(this.feedResponse);
    }
    processDelta(delta) {
        let s;
        for (let i = 0, len = delta.length; i < len; i++) {
            s = delta[i];
            if (!s || s[3] !== defaultNS.ll("meta")) {
                continue;
            }
            if (s[1] === defaultNS.http("statusCode")) {
                this.removeInvalidation(s[0]);
                this.setStatus(s[0], Number.parseInt(s[2].value, 10));
            }
            else if (s[1] === defaultNS.httph("Exec-Action")) {
                this.execExecHeader(s[2].value);
            }
        }
        return [];
    }
    /** Register a transformer so it can be used to interact with API's. */
    registerTransformer(transformer, mediaType, acceptValue) {
        const mediaTypes = Array.isArray(mediaType) ? mediaType : [mediaType];
        mediaTypes.forEach((type) => {
            pushToMap(this.mapping, type, transformer);
            this.accept.default = [this.accept.default, [type, acceptValue].join(";")].join();
        });
    }
    queueDelta(delta, subjects) {
        this.deltas.push(delta);
        const status = queuedDeltaStatus(1);
        for (const s of subjects) {
            if (!this.statusMap[s]) {
                this.statusMap[s] = status;
            }
        }
    }
    setAcceptForHost(origin, acceptValue) {
        this.accept[new URL(origin).origin] = acceptValue;
    }
    acceptForHost(iri) {
        return this.accept[new URL(typeof iri === "string" ? iri : iri.value).origin]
            || this.accept.default;
    }
    execExecHeader(actionsHeader) {
        if (actionsHeader) {
            const actions = actionsHeader.split(", ");
            for (let i = 0; i < actions.length; i++) {
                this.dispatch(NamedNode.find(actions[i]), undefined);
            }
        }
    }
    feedResponse(res, expedite = false) {
        if (res.status >= INTERNAL_SERVER_ERROR) {
            return Promise.reject(res);
        }
        const format = getContentType(res);
        const formatProcessors = this.mapping[format];
        const processor = formatProcessors && formatProcessors[0];
        if (processor === undefined) {
            return Promise.resolve([]);
        }
        res.expedite = expedite;
        return processor(res);
    }
    memoizeStatus(iri, s) {
        this.statusMap[iri.sI] = s;
        return s;
    }
    processExecAction(res) {
        const actionsHeader = getHeader(res, "Exec-Action");
        this.execExecHeader(actionsHeader);
        return Promise.resolve(res);
    }
    removeInvalidation(subject) {
        this.invalidationMap.delete(subject.sI);
    }
    setStatus(iri, status) {
        const url = this.fetchableURLFromIRI(iri);
        const prevStatus = this.statusMap[url.sI];
        this.store.touch(url);
        this.store.touch(iri);
        this.memoizeStatus(url, {
            lastRequested: new Date(),
            lastResponseHeaders: null,
            requested: true,
            status,
            timesRequested: prevStatus ? prevStatus.timesRequested + 1 : 1,
        });
    }
}

const CI_MATCH_PREFIX = 0;
const CI_MATCH_SUFFIX = 1;
/**
 * Expands a property if it's in short-form while preserving long-form.
 * Note: The vocabulary needs to be present in the store prefix library
 * @param prop The short- or long-form property
 * @param namespaces Object of namespaces by their abbreviation.
 * @returns The (expanded) property
 */
function expandProperty(prop, namespaces = defaultNS) {
    if (prop instanceof NamedNode || typeof prop === "undefined") {
        return prop;
    }
    if (typeof prop === "object") {
        if (prop.termType === "NamedNode") {
            return NamedNode.find(prop.value);
        }
        return undefined;
    }
    if (prop.indexOf("/") >= 1) {
        if (prop.startsWith("<") && prop.endsWith(">")) {
            return NamedNode.find(prop.slice(1, -1));
        }
        return NamedNode.find(prop);
    }
    const matches = prop.split(":");
    const constructor = namespaces[matches[CI_MATCH_PREFIX]];
    return constructor && constructor(matches[CI_MATCH_SUFFIX]);
}

const BASE = 36;
const DEC_CUTOFF = 2;
const IRI_LEN = 20;
function isPlainObject(o) {
    return typeof o === "object"
        && o !== null
        && !NON_DATA_OBJECTS_CTORS.find((c) => typeof o.prototype !== "undefined" && o instanceof c)
        && !Object.prototype.hasOwnProperty.call(o, "termType");
}
function isIterable(o) {
    return Array.isArray(o) || o instanceof Set;
}
function uploadIRI() {
    return defaultNS.ll(`blobs/a${Math.random().toString(BASE).substr(DEC_CUTOFF, IRI_LEN)}`);
}
/**
 * Converts an array to an RDF list-shaped {DataObject} for serialization.
 */
function list(arr) {
    return arr.reduceRight((acc, next) => ({
        [defaultNS.rdf.first.toString()]: next,
        [defaultNS.rdf.rest.toString()]: acc,
    }), defaultNS.rdf.nil);
}
/**
 * Converts an array to an RDF sequence-shaped {DataObject} for serialization.
 */
function seq(arr, id) {
    const base = { [defaultNS.rdf.type.toString()]: defaultNS.rdf.Seq };
    if (id) {
        base["@id"] = id;
    }
    return arr.reduce((acc, next, n) => Object.assign(acc, { [defaultNS.rdf(`_${n}`).toString()]: next }), base);
}
/** @private */
function processObject(subject, predicate, datum, graph) {
    let blobs = [];
    if (isIterable(datum)) {
        for (const subResource of datum) {
            if (isPlainObject(subResource)) {
                const id = subResource["@id"] || new BlankNode();
                blobs = blobs.concat(processDataObject(id, subResource, graph));
                graph.add(subject, predicate, id);
            }
            else {
                blobs = blobs.concat(processObject(subject, predicate, subResource, graph));
            }
        }
    }
    else if (typeof datum === "string"
        || typeof datum === "number"
        || typeof datum === "boolean"
        || datum instanceof Date) {
        graph.add(subject, predicate, Literal.fromValue(datum));
    }
    else if (datum instanceof File) {
        const f = uploadIRI();
        const file = new Statement(subject, predicate, f);
        blobs.push([f, datum]);
        graph.add(file);
    }
    else if (isPlainObject(datum)) {
        const id = datum["@id"] || new BlankNode();
        blobs = blobs.concat(processDataObject(id, datum, graph));
        graph.add(subject, predicate, id);
    }
    else if (datum && datum.termType === "NamedNode") {
        graph.add(subject, predicate, NamedNode.find(datum.value));
    }
    else if (datum && datum.termType === "Literal") {
        graph.add(subject, predicate, Literal.find(datum.value, datum.language, NamedNode.find(datum.datatype.value)));
    }
    else if (datum !== null && datum !== undefined) {
        graph.add(subject, predicate, Literal.fromValue(datum));
    }
    return blobs;
}
function processDataObject(subject, data, graph) {
    let blobs = [];
    const keys = Object.keys(data);
    for (let i = 0; i < keys.length; i++) {
        if (keys[i] === "@id") {
            continue;
        }
        const predicate = expandProperty(keys[i], defaultNS);
        const datum = data[keys[i]];
        if (predicate === undefined) {
            throw new Error(`Unknown predicate ${keys[i]} given (for subject '${subject}').`);
        }
        blobs = blobs.concat(processObject(subject, predicate, datum, graph));
    }
    return blobs;
}
function dataToGraphTuple(data) {
    const g = new IndexedFormula();
    const blobs = processDataObject(MAIN_NODE_DEFAULT_IRI, data, g);
    return [g, blobs];
}
/**
 * Convert a DataObject into a graph. Useful for writing test data in semi-plain JS objects
 * @param iriOrData The data object or an iri for the top-level object.
 * @param data The data object if an IRI was passed.
 * @param graph A graph to write the statements into.
 */
function toGraph(iriOrData, data, graph) {
    const passedIRI = iriOrData instanceof BlankNode || iriOrData instanceof NamedNode;
    if (passedIRI && !data) {
        throw new TypeError("Only an IRI was passed to `toObject`, a valid data object has to be the second argument");
    }
    const embeddedIRI = (passedIRI ? data : iriOrData)["@id"];
    let iri;
    if (embeddedIRI) {
        if (typeof embeddedIRI !== "string") {
            throw new TypeError("Embedded IRI (`@id`) value must be of type string");
        }
        iri = new NamedNode(embeddedIRI);
    }
    else {
        iri = passedIRI ? iriOrData : new BlankNode();
    }
    const dataObj = passedIRI ? data : iriOrData;
    const g = graph || new IndexedFormula();
    const blobs = processDataObject(iri, dataObj, g);
    return [iri, g, blobs];
}

const EMPTY_ST_ARR = Object.freeze([]);
/**
 * Provides a clean consistent interface to stored (RDF) data.
 */
class RDFStore {
    constructor() {
        this.changeBuffer = new Array(100);
        this.changeBufferCount = 0;
        /**
         * Record of the last time a resource was flushed.
         *
         * @note Not to be confused with the last change in the store, which might be later than the flush time.
         */
        this.changeTimestamps = new Uint32Array(0x100000);
        this.typeCache = [];
        this.deltas = [];
        this.langPrefs = Array.from(typeof navigator !== "undefined"
            ? (navigator.languages || [navigator.language])
            : ["en"]);
        this.store = graph();
        this.addGraphIRIS = [defaultNS.ll("add")];
        this.replaceGraphIRIS = [
            undefined,
            defaultNS.ll("replace"),
            this.store.defaultGraphIRI,
        ];
        this.removeGraphIRIS = [defaultNS.ll("remove")];
        this.processDelta = this.processDelta.bind(this);
        const g = graph();
        this.store = patchRDFLibStoreWithOverrides(g, this);
        this.store.newPropertyAction(defaultNS.rdf("type"), this.processTypeStatement.bind(this));
    }
    /**
     * Add statements to the store.
     * @param data Data to parse and add to the store.
     */
    addStatements(data) {
        if (!Array.isArray(data)) {
            throw new TypeError("An array of statements must be passed to addStatements");
        }
        for (let i = 0, len = data.length; i < len; i++) {
            this.store.addStatement(data[i]);
        }
    }
    addQuads(data) {
        const statements = new Array(data.length);
        for (let i = 0, len = data.length; i < len; i++) {
            statements[i] = this.store.add(data[i][0], data[i][1], data[i][2], data[i][3]);
        }
        return statements;
    }
    any(subj, pred, obj, why) {
        return this.store.any(subj, pred, obj, why);
    }
    anyStatementMatching(subj, pred, obj, why) {
        return this.store.anyStatementMatching(subj, pred, obj, why);
    }
    anyValue(subj, pred, obj, why) {
        return this.store.anyValue(subj, pred, obj, why);
    }
    canon(term) {
        return this.store.canon(term);
    }
    /**
     * Flushes the change buffer to the return value.
     * @return Statements held in memory since the last flush.
     */
    flush() {
        const deltas = this.deltas;
        this.deltas = [];
        for (let i = 0; i < deltas.length; i++) {
            this.processDelta(deltas[i]);
        }
        if (this.changeBufferCount === 0) {
            return EMPTY_ST_ARR;
        }
        const processingBuffer = this.changeBuffer;
        this.changeBuffer = new Array(100);
        this.changeBufferCount = 0;
        const changeStamp = Date.now();
        processingBuffer
            .filter((s) => {
            this.changeTimestamps[s.subject.sI] = changeStamp;
            return s.predicate === defaultNS.rdf("type");
        })
            .map((s) => this.processTypeStatement(undefined, s.subject, s.predicate, undefined, undefined));
        return processingBuffer;
    }
    /** @private */
    getInternalStore() {
        return this.store;
    }
    match(subj, pred, obj, why) {
        return this.store.match(subj, pred, obj, why) || [];
    }
    processDelta(delta) {
        const addables = [];
        const replacables = [];
        const removables = [];
        let quad;
        for (let i = 0, len = delta.length; i < len; i++) {
            quad = delta[i];
            if (!quad) {
                continue;
            }
            if (this.addGraphIRIS.findIndex((v) => quad[3] === v || quad[3].value.startsWith(v.value)) >= 0) {
                const g = new URL(quad[3].value).searchParams.get("graph");
                if (g) {
                    addables.push([quad[0], quad[1], quad[2], new NamedNode(g)]);
                }
                else {
                    addables.push(quad);
                }
            }
            else if (this.replaceGraphIRIS.includes(quad[3])
                || this.replaceGraphIRIS.findIndex((v) => quad[3] === v
                    || quad[3].value.startsWith(v && v.value)) >= 0) {
                const g = new URL(quad[3].value).searchParams.get("graph");
                if (g) {
                    replacables.push([quad[0], quad[1], quad[2], new NamedNode(g)]);
                }
                else {
                    replacables.push(quad);
                }
            }
            else if (this.removeGraphIRIS.includes(quad[3])) {
                const matches = this.store.match(quad[0], quad[1], null, null);
                removables.push(...matches);
            }
        }
        this.removeStatements(removables);
        return this.replaceMatches(replacables).concat(this.addQuads(addables));
    }
    removeResource(subject) {
        this.touch(subject);
        this.typeCache[subject.sI] = [];
        this.removeStatements(this.statementsFor(subject));
    }
    removeStatements(statements) {
        this.store.remove(statements.slice());
    }
    /**
     * Removes an array of statements and inserts another.
     * Note: Be sure that the replacement contains the same subjects as the original to let the
     *  broadcast work correctly.
     * @access private This is in conflict with the typescript declaration due to the development of some experimental
     *                  features, but this method shouldn't be used nevertheless.
     * @param original The statements to remove from the store.
     * @param replacement The statements to add to the store.
     */
    replaceStatements(original, replacement) {
        const uniqueStatements = new Array(replacement.length).filter(Boolean);
        for (let i = 0; i < replacement.length; i++) {
            const cond = original.some((o) => o.subject.sameTerm(replacement[i].subject) && o.predicate.sameTerm(replacement[i].predicate));
            if (!cond) {
                uniqueStatements.push(replacement[i]);
            }
        }
        this.removeStatements(original);
        // Remove statements not in the old object. Useful for replacing data loosely related to the main resource.
        for (let i = 0; i < uniqueStatements.length; i++) {
            this.store.removeMatches(uniqueStatements[i].subject, uniqueStatements[i].predicate);
        }
        return this.addStatements(replacement);
    }
    replaceMatches(statements) {
        for (let i = 0; i < statements.length; i++) {
            this.removeStatements(this.match(statements[i][0], statements[i][1], undefined, undefined));
        }
        const matches = new Array(statements.length);
        for (let i = 0, len = statements.length; i < len; i++) {
            matches[i] = this.store.add(statements[i][0], statements[i][1], statements[i][2], statements[i][3]);
        }
        return matches;
    }
    getResourcePropertyRaw(subject, property) {
        const props = this.statementsFor(subject);
        if (Array.isArray(property)) {
            for (let i = 0; i < property.length; i++) {
                const values = allRDFPropertyStatements(props, property[i]);
                if (values.length > 0) {
                    return values;
                }
            }
            return EMPTY_ST_ARR;
        }
        return allRDFPropertyStatements(props, property);
    }
    getResourceProperties(subject, property) {
        if (property === defaultNS.rdf("type")) {
            return this.typeCache[subject.sI] || [];
        }
        return this
            .getResourcePropertyRaw(subject, property)
            .map((s) => s.object);
    }
    getResourceProperty(subject, property) {
        if (property === defaultNS.rdf("type")) {
            const entry = this.typeCache[subject.sI];
            return entry ? entry[0] : undefined;
        }
        const rawProp = this.getResourcePropertyRaw(subject, property);
        if (rawProp.length === 0) {
            return undefined;
        }
        return getPropBestLang(rawProp, this.langPrefs);
    }
    queueDelta(delta) {
        this.deltas.push(delta);
    }
    /**
     * Searches the store for all the statements on {iri} (so not all statements relating to {iri}).
     * @param subject The identifier of the resource.
     */
    statementsFor(subject) {
        const canon = this.store.canon(subject).sI;
        return typeof this.store.subjectIndex[canon] !== "undefined"
            ? this.store.subjectIndex[canon]
            : EMPTY_ST_ARR;
    }
    touch(iri) {
        this.changeTimestamps[iri.sI] = Date.now();
        this.changeBuffer.push(new Statement(iri, defaultNS.ll("nop"), defaultNS.ll("nop")));
        this.changeBufferCount++;
    }
    workAvailable() {
        return this.deltas.length + this.changeBufferCount;
    }
    /**
     * Builds a cache of types per resource. Can be omitted when compiled against a well known service.
     */
    processTypeStatement(_formula, subj, _pred, obj, _why) {
        if (!Array.isArray(this.typeCache[subj.sI])) {
            this.typeCache[subj.sI] = [obj];
            return false;
        }
        this.typeCache[subj.sI] = this.statementsFor(subj)
            .filter((s) => s.predicate === defaultNS.rdf("type"))
            .map((s) => s.object);
        if (obj) {
            this.typeCache[subj.sI].push(obj);
        }
        return false;
    }
}

/*
 * TODO: All of them...
 */
// const nsOWLAllDifferent = NS.owl("AllDifferent");
// const nsOWLAllDisjointClasses = NS.owl("AllDisjointClasses");
// const nsOWLAllDisjointProperties = NS.owl("AllDisjointProperties");
// const nsOWLallValuesFrom = NS.owl("allValuesFrom");
// const nsOWLannotatedProperty = NS.owl("annotatedProperty");
// const nsOWLannotatedSource = NS.owl("annotatedSource");
// const nsOWLannotatedTarget = NS.owl("annotatedTarget");
// const nsOWLAnnotation = NS.owl("Annotation");
// const nsOWLAnnotationProperty = NS.owl("AnnotationProperty");
// const nsOWLassertionProperty = NS.owl("assertionProperty");
// const nsOWLAsymmetricProperty = NS.owl("AsymmetricProperty");
// const nsOWLAxiom = NS.owl("Axiom");
// const nsOWLbackwardCompatibleWith = NS.owl("backwardCompatibleWith");
// const nsOWLbottomDataProperty = NS.owl("bottomDataProperty");
// const nsOWLbottomObjectProperty = NS.owl("bottomObjectProperty");
// const nsOWLcardinality = NS.owl("cardinality");
// const nsOWLClass = NS.owl("Class");
// const nsOWLcomplementOf = NS.owl("complementOf");
// const nsOWLDataRange = NS.owl("DataRange");
// const nsOWLdatatypeComplementOf = NS.owl("datatypeComplementOf");
// const nsOWLDatatypeProperty = NS.owl("DatatypeProperty");
// const nsOWLdeprecated = NS.owl("deprecated");
// const nsOWLDeprecatedClass = NS.owl("DeprecatedClass");
// const nsOWLDeprecatedProperty = NS.owl("DeprecatedProperty");
// const nsOWLdifferentFrom = NS.owl("differentFrom");
// const nsOWLdisjointUnionOf = NS.owl("disjointUnionOf");
// const nsOWLdisjointWith = NS.owl("disjointWith");
// const nsOWLdistinctMembers = NS.owl("distinctMembers");
// const nsOWLequivalentClass = NS.owl("equivalentClass");
// const nsOWLequivalentProperty = NS.owl("equivalentProperty");
// const nsOWLFunctionalProperty = NS.owl("FunctionalProperty");
// const nsOWLhasKey = NS.owl("hasKey");
// const nsOWLhasSelf = NS.owl("hasSelf");
// const nsOWLhasValue = NS.owl("hasValue");
// const nsOWLimports = NS.owl("imports");
// const nsOWLincompatibleWith = NS.owl("incompatibleWith");
// const nsOWLintersectionOf = NS.owl("intersectionOf");
// const nsOWLInverseFunctionalProperty = NS.owl("InverseFunctionalProperty");
// const nsOWLinverseOf = NS.owl("inverseOf");
// const nsOWLIrreflexiveProperty = NS.owl("IrreflexiveProperty");
// const nsOWLmaxCardinality = NS.owl("maxCardinality");
// const nsOWLmaxQualifiedCardinality = NS.owl("maxQualifiedCardinality");
// const nsOWLmembers = NS.owl("members");
// const nsOWLminCardinality = NS.owl("minCardinality");
// const nsOWLminQualifiedCardinality = NS.owl("minQualifiedCardinality");
// const nsOWLNamedIndividual = NS.owl("NamedIndividual");
// const nsOWLNegativePropertyAssertion = NS.owl("NegativePropertyAssertion");
// const nsOWLNothing = NS.owl("Nothing");
// const nsOWLObjectProperty = NS.owl("ObjectProperty");
// const nsOWLonClass = NS.owl("onClass");
// const nsOWLonDataRange = NS.owl("onDataRange");
// const nsOWLonDatatype = NS.owl("onDatatype");
// const nsOWLoneOf = NS.owl("oneOf");
// const nsOWLonProperty = NS.owl("onProperty");
// const nsOWLonProperties = NS.owl("onProperties");
// const nsOWLOntology = NS.owl("Ontology");
// const nsOWLOntologyProperty = NS.owl("OntologyProperty");
// const nsOWLpriorVersion = NS.owl("priorVersion");
// const nsOWLpropertyChainAxiom = NS.owl("propertyChainAxiom");
// const nsOWLpropertyDisjointWith = NS.owl("propertyDisjointWith");
// const nsOWLqualifiedCardinality = NS.owl("qualifiedCardinality");
// const nsOWLReflexiveProperty = NS.owl("ReflexiveProperty");
// const nsOWLRestriction = NS.owl("Restriction");
const nsOWLsameAs = defaultNS.owl("sameAs");
// const nsOWLsomeValuesFrom = NS.owl("someValuesFrom");
// const nsOWLsourceIndividual = NS.owl("sourceIndividual");
// const nsOWLSymmetricProperty = NS.owl("SymmetricProperty");
// const nsOWLtargetIndividual = NS.owl("targetIndividual");
// const nsOWLtargetValue = NS.owl("targetValue");
// const nsOWLThing = NS.owl("Thing");
// const nsOWLtopDataProperty = NS.owl("topDataProperty");
// const nsOWLtopObjectProperty = NS.owl("topObjectProperty");
// const nsOWLTransitiveProperty = NS.owl("TransitiveProperty");
// const nsOWLunionOf = NS.owl("unionOf");
// const nsOWLversionInfo = NS.owl("versionInfo");
// const nsOWLversionIRI = NS.owl("versionIRI");
// const nsOWLwithRestrictions = NS.owl("withRestrictions");
const OWL = {
    axioms: [],
    processStatement(item, ctx) {
        if (item.predicate.equals(nsOWLsameAs)) {
            const a = ctx.equivalenceSet.add(item.object.sI);
            const b = ctx.equivalenceSet.add(item.subject.sI);
            ctx.equivalenceSet.union(a, b);
            return [item];
        }
        return null;
    },
    processType(_, __) {
        return false;
    },
};

const nsRDFProperty = defaultNS.rdf("Property");
const nsRDFSClass = defaultNS.rdfs("Class");
const nsRDFSResource = defaultNS.rdfs("Resource");
const nsRDFSsubClassOf = defaultNS.rdfs("subClassOf");
const nsRDFSdomain = defaultNS.rdfs("domain");
const nsRDFSrange = defaultNS.rdfs("range");
const nsRDFSsubPropertyOf = defaultNS.rdfs("subPropertyOf");
const nsRDFtype = defaultNS.rdf("type");
/**
 * Implements the RDF/RDFS axioms and rules.
 * @type {VocabularyProcessor}
 */
const RDFS = {
    axioms: [
        new Statement(nsRDFtype, nsRDFSdomain, nsRDFSResource),
        new Statement(nsRDFSdomain, nsRDFSdomain, nsRDFProperty),
        new Statement(nsRDFSrange, nsRDFSdomain, nsRDFProperty),
        new Statement(nsRDFSsubPropertyOf, nsRDFSdomain, nsRDFProperty),
        new Statement(nsRDFSsubClassOf, nsRDFSdomain, nsRDFSClass),
        new Statement(defaultNS.rdf("subject"), nsRDFSdomain, defaultNS.rdf("Statement")),
        new Statement(defaultNS.rdf("predicate"), nsRDFSdomain, defaultNS.rdf("Statement")),
        new Statement(defaultNS.rdf("object"), nsRDFSdomain, defaultNS.rdf("Statement")),
        new Statement(defaultNS.rdfs("member"), nsRDFSdomain, nsRDFSResource),
        new Statement(defaultNS.rdf("first"), nsRDFSdomain, defaultNS.rdf("List")),
        new Statement(defaultNS.rdf("rest"), nsRDFSdomain, defaultNS.rdf("List")),
        new Statement(defaultNS.rdfs("seeAlso"), nsRDFSdomain, nsRDFSResource),
        new Statement(defaultNS.rdfs("isDefinedBy"), nsRDFSdomain, nsRDFSResource),
        new Statement(defaultNS.rdfs("comment"), nsRDFSdomain, nsRDFSResource),
        new Statement(defaultNS.rdfs("label"), nsRDFSdomain, nsRDFSResource),
        new Statement(defaultNS.rdf("value"), nsRDFSdomain, nsRDFSResource),
        new Statement(nsRDFtype, nsRDFSrange, nsRDFSClass),
        new Statement(nsRDFSdomain, nsRDFSrange, nsRDFSClass),
        new Statement(nsRDFSrange, nsRDFSrange, nsRDFSClass),
        new Statement(nsRDFSsubPropertyOf, nsRDFSrange, nsRDFProperty),
        new Statement(nsRDFSsubClassOf, nsRDFSrange, nsRDFSClass),
        new Statement(defaultNS.rdf("subject"), nsRDFSrange, nsRDFSResource),
        new Statement(defaultNS.rdf("predicate"), nsRDFSrange, nsRDFSResource),
        new Statement(defaultNS.rdf("object"), nsRDFSrange, nsRDFSResource),
        new Statement(defaultNS.rdfs("member"), nsRDFSrange, nsRDFSResource),
        new Statement(defaultNS.rdf("first"), nsRDFSrange, nsRDFSResource),
        new Statement(defaultNS.rdf("rest"), nsRDFSrange, defaultNS.rdf("List")),
        new Statement(defaultNS.rdfs("seeAlso"), nsRDFSrange, nsRDFSResource),
        new Statement(defaultNS.rdfs("isDefinedBy"), nsRDFSrange, nsRDFSResource),
        new Statement(defaultNS.rdfs("comment"), nsRDFSrange, defaultNS.rdfs("Literal")),
        new Statement(defaultNS.rdfs("label"), nsRDFSrange, defaultNS.rdfs("Literal")),
        new Statement(defaultNS.rdf("value"), nsRDFSrange, nsRDFSResource),
        new Statement(defaultNS.rdf("Alt"), nsRDFSsubClassOf, defaultNS.rdfs("Container")),
        new Statement(defaultNS.rdf("Bag"), nsRDFSsubClassOf, defaultNS.rdfs("Container")),
        new Statement(defaultNS.rdf("Seq"), nsRDFSsubClassOf, defaultNS.rdfs("Container")),
        new Statement(defaultNS.rdfs("ContainerMembershipProperty"), nsRDFSsubClassOf, nsRDFProperty),
        new Statement(defaultNS.rdfs("isDefinedBy"), nsRDFSsubPropertyOf, defaultNS.rdfs("seeAlso")),
        new Statement(defaultNS.rdfs("Datatype"), nsRDFSsubClassOf, nsRDFSClass),
        new Statement(nsRDFSResource, nsRDFtype, nsRDFSClass),
        new Statement(nsRDFSClass, nsRDFtype, nsRDFSClass),
    ],
    processStatement(item, ctx) {
        const result = [item];
        const domainStatements = ctx.store.statementsMatching(item.predicate, nsRDFSdomain);
        if (domainStatements.length > 0) {
            for (let i = 0; i < domainStatements.length; i++) {
                result.push(new Statement(item.subject, nsRDFtype, domainStatements[i].object));
            }
        }
        const rangeStatements = ctx.store.statementsMatching(item.predicate, nsRDFSrange);
        if (rangeStatements.length > 0) { // P rdfs:range C..Cn
            for (let i = 0; i < rangeStatements.length; i++) {
                result.push(new Statement(item.object, nsRDFtype, rangeStatements[i].object));
            }
        }
        if (nsRDFSdomain.equals(item.predicate)) {
            if (!(item.object instanceof NamedNode)) {
                throw new TypeError(`A non IRI was passed as object to rdfs:domain (was: ${item.object}).`);
            }
            result.push(new Statement(item.subject, nsRDFtype, nsRDFProperty)); // P rdf:type rdf:Property
            result.push(new Statement(item.object, nsRDFtype, nsRDFSClass)); // C rdf:type rdfs:Class
            const dereferences = ctx.store.statementsMatching(item.subject);
            for (let i = 0; i < dereferences.length; i++) {
                result.push(new Statement(item.subject, nsRDFtype, dereferences[i].object));
            }
        }
        else if (nsRDFSrange.equals(item.predicate)) {
            if (!(item.object instanceof NamedNode)) {
                throw new TypeError(`A non IRI was passed as object to rdfs:domain (was: ${item.object}).`);
            }
            result.push(new Statement(item.subject, nsRDFtype, nsRDFProperty)); // P rdf:type rdf:Property
            result.push(new Statement(item.object, nsRDFtype, nsRDFSClass)); // C rdf:type rdfs:Class
            const dereferences = ctx.store.statementsMatching(undefined, undefined, item.subject);
            for (let i = 0; i < dereferences.length; i++) {
                result.push(new Statement(dereferences[i].subject, nsRDFtype, item.object));
            }
        }
        else if (nsRDFSsubClassOf.equals(item.predicate)) { // C1 rdfs:subClassOf C2
            if (!(item.object instanceof NamedNode || item.object instanceof BlankNode)) {
                throw new Error("Object of subClassOf statement must be a NamedNode");
            }
            const iSubject = item.subject.sI;
            const iObject = item.object.sI;
            if (!ctx.superMap.has(iObject)) {
                ctx.superMap.set(iObject, new Set([nsRDFSResource.sI]));
            }
            let parents = ctx.superMap.get(iObject);
            if (parents === undefined) {
                parents = new Set();
                ctx.superMap.set(iObject, parents);
            }
            parents.add(iObject);
            const itemVal = ctx.superMap.get(iSubject) || new Set([iSubject]);
            parents.forEach((i) => itemVal.add(i));
            ctx.superMap.set(iSubject, itemVal);
            ctx.superMap.forEach((v, k) => {
                if (k !== iSubject && v.has(iSubject)) {
                    itemVal.forEach(v.add, v);
                }
            });
        }
        else if (nsRDFSsubPropertyOf.equals(item.predicate)) {
            // TODO: Implement
            return result;
        }
        return result.length === 1 ? null : result;
    },
    processType(type, ctx) {
        RDFS.processStatement(new Statement(type, nsRDFSsubClassOf, nsRDFSResource), ctx);
        ctx.store.add(new Statement(type, nsRDFtype, nsRDFSClass));
        return false;
    },
};

const nsRDFSsubClassOf$1 = defaultNS.rdfs("subClassOf");
const ianaMT = Namespace("http://www.w3.org/ns/iana/media-types/");
const ianaJSONResource = ianaMT(`${F_JSON}#Resource`);
const ianaJSONLDResource = ianaMT(`${F_JSONLD}#Resource`);
const ianaN3Resource = ianaMT(`${F_N3}#Resource`);
const ianaNTResource = ianaMT(`${F_NTRIPLES}#Resource`);
const ianaNQResource = ianaMT(`${F_NQUADS}#Resource`);
const ianaPlainResource = ianaMT(`${F_PLAIN}#Resource`);
const ianaRDFXMLResource = ianaMT(`${F_RDF_XML}#Resource`);
const ianaTTLResource = ianaMT(`${F_TURTLE}#Resource`);
/**
 * Implements the assumptions done by rdflib.js in various parts of their code.
 */
const RDFLIB = {
    axioms: [
        new Statement(defaultNS.link("Document"), nsRDFSsubClassOf$1, nsRDFSResource),
        new Statement(ianaJSONResource, nsRDFSsubClassOf$1, nsRDFSResource),
        new Statement(ianaJSONLDResource, nsRDFSsubClassOf$1, nsRDFSResource),
        new Statement(ianaN3Resource, nsRDFSsubClassOf$1, nsRDFSResource),
        new Statement(ianaNTResource, nsRDFSsubClassOf$1, nsRDFSResource),
        new Statement(ianaNQResource, nsRDFSsubClassOf$1, nsRDFSResource),
        new Statement(ianaPlainResource, nsRDFSsubClassOf$1, nsRDFSResource),
        new Statement(ianaRDFXMLResource, nsRDFSsubClassOf$1, nsRDFSResource),
        new Statement(ianaTTLResource, nsRDFSsubClassOf$1, nsRDFSResource),
    ],
    processStatement(_, __) {
        return null;
    },
    processType(_, __) {
        return false;
    },
};

/**
 * DisjointSet
 * Module port from https://github.com/mljs/disjoint-set/
 */
/**
 * @class DisjointSet
 */
class DisjointSet {
    constructor() {
        this.nodes = new Map();
    }
    /**
     * Adds an element as a new set
     * @param {*} value
     * @return {DisjointSetNode} Object holding the element
     */
    add(value) {
        let node = this.nodes.get(value);
        if (!node) {
            node = new DisjointSetNode(value);
            this.nodes.set(value, node);
        }
        return node;
    }
    /**
     * Merges the sets that contain x and y
     * @param {DisjointSetNode} x
     * @param {DisjointSetNode} y
     */
    union(x, y) {
        const rootX = this.find(x);
        const rootY = this.find(y);
        if (rootX === rootY) {
            return;
        }
        if (rootX.rank < rootY.rank) {
            rootX.parent = rootY;
        }
        else if (rootX.rank > rootY.rank) {
            rootY.parent = rootX;
        }
        else {
            rootY.parent = rootX;
            rootX.rank++;
        }
    }
    /**
     * Finds and returns the root node of the set that contains node
     * @param {DisjointSetNode} node
     * @return {DisjointSetNode}
     */
    find(node) {
        let rootX = node;
        while (rootX.parent !== null) {
            rootX = rootX.parent;
        }
        let toUpdateX = node;
        while (toUpdateX.parent !== null) {
            const toUpdateParent = toUpdateX;
            toUpdateX = toUpdateX.parent;
            toUpdateParent.parent = rootX;
        }
        return rootX;
    }
    /**
     * Returns true if x and y belong to the same set
     * @param {DisjointSetNode} x
     * @param {DisjointSetNode} y
     */
    connected(x, y) {
        return this.find(x) === this.find(y);
    }
}
// tslint:disable-next-line max-classes-per-file
class DisjointSetNode {
    constructor(value) {
        this.value = value;
        this.parent = null;
        this.rank = 0;
    }
}

/**
 * Implements some RDF/OWL logic to enhance the functionality of the property lookups.
 *
 * Basically duplicates some functionality already present in {IndexedFormula} IIRC, but this API should be more
 * optimized so it can be used in real-time by low-power devices as well.
 */
class Schema extends IndexedFormula {
    constructor(liveStore) {
        super();
        this.equivalenceSet = new DisjointSet();
        this.superMap = new Map();
        this.processedTypes = [];
        this.liveStore = liveStore;
        this.expansionCache = [];
        for (let i = 0; i < Schema.vocabularies.length; i++) {
            this.addStatements(Schema.vocabularies[i].axioms);
        }
    }
    /** Push statements onto the graph so it can be used by the render store for component determination. */
    addStatements(statements) {
        const unique = statements.filter((s) => !this.holdsStatement(s));
        const eligible = unique.filter(this.process.bind(this));
        if (eligible.length === 0) {
            return;
        }
        this.addAll(eligible);
        return this.liveStore.addStatements(eligible);
    }
    expand(types) {
        if (types.length === 1) {
            const existing = this.expansionCache[types[0]];
            return this.expansionCache[types[0]] = existing
                ? existing
                : this.sort(this.mineForTypes(types));
        }
        return this.sort(this.mineForTypes(types));
    }
    getProcessingCtx() {
        return {
            equivalenceSet: this.equivalenceSet,
            store: this,
            superMap: this.superMap,
        };
    }
    isInstanceOf(resource, superClass) {
        return this.holdsStatement(Statement.from(NamedNode.findByStoreIndex(resource), defaultNS.rdf("type"), NamedNode.findByStoreIndex(superClass)));
    }
    isSubclassOf(resource, superClass) {
        const resourceMap = this.superMap.get(resource);
        if (resourceMap) {
            return resourceMap.has(superClass);
        }
        return false;
    }
    /**
     * Returns the hierarchical depth of the type, or -1 if unknown.
     * @param type the type to check
     */
    superTypeDepth(type) {
        const superMap = this.superMap.get(type);
        return superMap ? superMap.size : -1;
    }
    /**
     * Expands the given lookupTypes to include all their equivalent and subclasses.
     * This is done in multiple iterations until no new types are found.
     * @param lookupTypes The types to look up. Once given, these are assumed to be classes.
     */
    mineForTypes(lookupTypes) {
        if (lookupTypes.length === 0) {
            return [nsRDFSResource.sI];
        }
        const canonicalTypes = [];
        for (let i = 0; i < lookupTypes.length; i++) {
            const canon = this.liveStore.canon(NamedNode.findByStoreIndex(lookupTypes[i])).sI;
            if (!this.processedTypes.includes(canon)) {
                for (let j = 0; j < Schema.vocabularies.length; j++) {
                    Schema.vocabularies[j].processType(NamedNode.findByStoreIndex(canon), this.getProcessingCtx());
                }
                this.processedTypes.push(canon);
            }
            if (!canonicalTypes.includes(canon)) {
                canonicalTypes.push(canon);
            }
        }
        const allTypes = canonicalTypes
            .reduce((a, b) => {
            const superSet = this.superMap.get(b);
            if (typeof superSet === "undefined") {
                return a;
            }
            superSet.forEach((s) => {
                if (!a.includes(s)) {
                    a.push(s);
                }
            });
            return a;
        }, [...lookupTypes]);
        return this.sort(allTypes);
    }
    sort(types) {
        return types.sort((a, b) => {
            if (this.isSubclassOf(a, b)) {
                return -1;
            }
            else if (this.isSubclassOf(b, a)) {
                return 1;
            }
            const aDepth = this.superTypeDepth(a);
            const bDepth = this.superTypeDepth(b);
            if (aDepth < bDepth) {
                return 1;
            }
            else if (aDepth > bDepth) {
                return -1;
            }
            return 0;
        });
    }
    process(item) {
        for (let i = 0; i < Schema.vocabularies.length; i++) {
            const res = Schema.vocabularies[i].processStatement(item, this.getProcessingCtx());
            if (res !== null) {
                return res;
            }
        }
        return null;
    }
}
Schema.vocabularies = [OWL, RDFS, RDFLIB];

class LinkedRenderStore {
    // tslint:disable-next-line no-object-literal-type-assertion
    constructor(opts = {}) {
        /**
         * Map of {ActionMap} which hold action dispatchers. Calling a dispatcher should execute the action, causing them to
         * be handled like any back-end sent action.
         *
         * Constructing action IRI's and dispatching them in user code was a bit hard, this object allows any middleware to
         * define their actions (within their namespace) in a code-oriented fashion. The middleware has control over how the
         * actions will be dispatched, but it should be the same as if a back-end would have executed the action (via the
         * Exec-Action header).
         */
        this.actions = {};
        /** Whenever a resource has no type, assume it to be this. */
        this.defaultType = defaultNS.schema("Thing");
        this.namespaces = Object.assign({}, defaultNS);
        this.bulkFetch = false;
        this.store = new RDFStore();
        this.bulkSubscriptions = [];
        this.subjectSubscriptions = [];
        if (opts.store) {
            this.store = opts.store;
        }
        this.report = opts.report || ((e) => { throw e; });
        this.api = opts.api || new DataProcessor({
            dispatch: opts.dispatch,
            report: this.report,
            store: this.store,
        });
        this.deltaProcessors = [this.api, this.store];
        if (opts.dispatch) {
            this.dispatch = opts.dispatch;
        }
        this.defaultType = opts.defaultType || defaultNS.schema("Thing");
        this.namespaces = opts.namespaces || Object.assign({}, defaultNS);
        this.schema = opts.schema || new Schema(this.store);
        this.mapping = opts.mapping || new ComponentStore(this.schema);
        this.resourceQueue = [];
        this.broadcast = this.broadcast.bind(this);
        this.processResourceQueue = this.processResourceQueue.bind(this);
    }
    static registerRenderer(component, type, prop = RENDER_CLASS_NAME, topology = DEFAULT_TOPOLOGY) {
        const types = normalizeType(type).map((n) => n.sI);
        const props = normalizeType(prop)
            .map((p) => (p || RENDER_CLASS_NAME).sI);
        const topologies = normalizeType(topology)
            .map((t) => (t || DEFAULT_TOPOLOGY).sI);
        return ComponentStore.registerRenderer(component, types, props, topologies);
    }
    get dispatch() {
        if (typeof this._dispatch === "undefined") {
            throw new Error("Invariant: cannot call `dispatch` before initialization is complete");
        }
        return this._dispatch;
    }
    set dispatch(value) {
        this._dispatch = value;
        this.api.dispatch = value;
    }
    /**
     * Push one or more ontological items onto the graph so it can be used by the render store for component
     * determination.
     *
     * Adding information after the initial render currently conflicts with the caching and will result in inconsistent
     * results.
     */
    addOntologySchematics(items) {
        this.schema.addStatements(items);
    }
    /**
     * Execute an Action by its IRI. This will result in an HTTP request being done and probably some state changes.
     * @param {module:rdflib.NamedNode} subject The resource to execute. Generally a schema:Action derivative with a
     *   schema:EntryPoint to describe the request. Currently schema:url is used over schema:urlTemplate
     *   to acquire the request URL, since template processing isn't implemented (yet).
     * @param {DataObject} data An object to send in the body when a non-safe method is used.
     * @return {Promise<LinkedActionResponse>}
     */
    execActionByIRI(subject, data) {
        const preparedData = dataToGraphTuple(data || {});
        return this
            .api
            .execActionByIRI(subject, preparedData)
            .then((res) => {
            this.broadcast(false, 100);
            return res;
        });
    }
    /**
     * Execute a resource.
     *
     * Every action will fall through the execution middleware layers.
     *
     * @see https://github.com/fletcher91/link-lib/wiki/%5BDesign-draft%5D-Actions,-data-streams,-and-middleware
     *
     * @param {NamedNode} subject The resource to execute (can be either an IRI or an URI)
     * @param {Object} args The arguments to the function defined by the subject.
     */
    async exec(subject, args) {
        return this.dispatch(subject, args);
    }
    /**
     * Convert a string value to a NamedNode if possible. Useful for looking op dynamic data like user input. Please
     * refrain from using in static code, as this will impact performance.
     */
    expandProperty(prop) {
        return expandProperty(prop, this.namespaces);
    }
    /**
     * Resolve the values at the end of the path
     * @param subject The resource to start descending on
     * @param path A list of linked predicates to descend on.
     */
    dig(subject, path) {
        if (path.length === 0) {
            return [];
        }
        const remaining = path.slice();
        const pred = remaining.shift();
        if (remaining.length === 0) {
            return this.getResourceProperties(subject, pred);
        }
        const props = this.getResourceProperties(subject, pred);
        if (props) {
            return props
                .map((term) => (term.termType === "NamedNode" || term.termType === "BlankNode")
                && this.dig(term, remaining))
                .flat(1)
                .filter(Boolean);
        }
        return [];
    }
    /**
     * Retrieve the subjects from {subject} to find all resources which have an object at the
     * end of the {path} which matches {match}.
     * @param subject The resource to start descending on.
     * @param path A list of linked predicates to descend on.
     * @param match The value which the predicate at the end of {path} has to match for its subject to return.
     */
    findSubject(subject, path, match) {
        if (path.length === 0) {
            return [];
        }
        const remaining = path.slice();
        const pred = remaining.shift();
        const props = this.getResourceProperties(subject, pred);
        if (props && remaining.length === 0) {
            const finder = Array.isArray(match)
                ? (p) => match.some((m) => m.equals(p))
                : (p) => match.equals(p);
            return props.find(finder) ? [subject] : [];
        }
        else if (props) {
            return props
                .map((term) => (term.termType === "NamedNode" || term.termType === "BlankNode")
                && this.findSubject(term, remaining, match))
                .flat(1)
                .filter(Boolean);
        }
        return [];
    }
    /**
     * Finds the best render component for a given property in respect to a topology.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @param type The type(s) of the resource to render.
     * @param predicate The predicate(s) (property(s)) to render.
     * @param [topology] The topology of the resource, if any
     * @returns The most appropriate renderer, if any.
     */
    getComponentForProperty(type = this.defaultType, predicate, topology = DEFAULT_TOPOLOGY) {
        if (type === undefined || (Array.isArray(type) && type.length === 0)) {
            return undefined;
        }
        const types = normalizeType(type).map((n) => n.sI);
        const predicates = normalizeType(predicate).map((n) => n.sI);
        return this.mapping.getRenderComponent(types, predicates, topology.sI, this.defaultType.sI);
    }
    /**
     * Finds the best render component for a type in respect to a topology.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @see LinkedRenderStore#getComponentForProperty
     * @param type The type(s) of the resource to render.
     * @param [topology] The topology of the resource, if any
     * @returns The most appropriate renderer, if any.
     */
    getComponentForType(type, topology = DEFAULT_TOPOLOGY) {
        return this.getComponentForProperty(type, RENDER_CLASS_NAME, topology);
    }
    queueEntity(iri, opts) {
        if (!(opts && opts.reload) && !this.shouldLoadResource(iri)) {
            return;
        }
        this.resourceQueue.push([iri, opts]);
        this.scheduleResourceQueue();
    }
    /**
     * Queue a linked-delta to be processed.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     */
    queueDelta(delta, expedite = false) {
        const quadArr = delta[0] instanceof Statement
            ? delta.map((s) => s.toQuad())
            : delta;
        const subjects = quadArr.reduce((acc, [s]) => acc.includes(s.sI) ? acc : acc.concat(s.sI), []);
        for (const dp of this.deltaProcessors) {
            dp.queueDelta(quadArr, subjects);
        }
        return this.broadcastWithExpedite(expedite);
    }
    /**
     * Will fetch the entity with the URL {iri}. When a resource under that subject is already present, it will not
     * be fetched again.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @param iri The SomeNode of the resource
     * @param opts The options for fetch-/processing the resource.
     * @return A promise with the resulting entity
     */
    async getEntity(iri, opts) {
        const apiOpts = {};
        let preExistingData;
        if (opts && opts.reload) {
            apiOpts.force = true;
            apiOpts.clearPreviousData = true;
            preExistingData = this.tryEntity(iri);
        }
        if (preExistingData !== undefined) {
            this.store.removeStatements(preExistingData);
        }
        await this.api.getEntity(iri, apiOpts);
        this.broadcast();
    }
    /**
     * Resolves all the properties {property} of resource {subject} to their statements.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @param {SomeNode} subject The resource to get the properties for.
     * @param {SomeNode | SomeNode[]} property
     * @return {Statement[]} All the statements of {property} on {subject}, or an empty array when none are present.
     */
    getResourcePropertyRaw(subject, property) {
        return this.store.getResourcePropertyRaw(subject, property);
    }
    /**
     * Resolves all the properties {property} of resource {subject} to a value.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @param {SomeNode} subject The resource to get the properties for.
     * @param {SomeNode | SomeNode[]} property
     * @return {SomeTerm[]} The resolved values of {property}, or an empty array when none are present.
     */
    getResourceProperties(subject, property) {
        return this.store.getResourceProperties(subject, property);
    }
    /**
     * Resolves the property {property} of resource {subject} to a value.
     *
     * When more than one statement on {property} is present, a random one will be chosen. See
     * {LinkedResourceContainer#getResourceProperties} to retrieve all the values.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @param {SomeNode} subject The resource to get the properties for.
     * @param {SomeNode | SomeNode[]} property
     * @return {SomeTerm | undefined} The resolved value of {property}, or undefined when none are present.
     */
    getResourceProperty(subject, property) {
        return this.store.getResourceProperty(subject, property);
    }
    getStatus(iri) {
        if (iri.termType === "BlankNode") {
            return emptyRequest;
        }
        if (this.resourceQueue.find(([resource]) => resource === iri)) {
            return {
                lastRequested: new Date(),
                lastResponseHeaders: null,
                requested: true,
                status: 202,
                timesRequested: 1,
            };
        }
        return this.api.getStatus(iri);
    }
    processDelta(delta, expedite = false) {
        const quadArr = delta[0] instanceof Statement
            ? delta.map((s) => s.toQuad())
            : delta;
        const statements = this.deltaProcessors
            .reduce((acc, dp) => acc.concat(dp.processDelta(quadArr)), []);
        return this.broadcastWithExpedite(expedite)
            .then(() => statements);
    }
    /**
     * Bulk register components formatted with {LinkedRenderStore.registerRenderer}.
     * @see LinkedRenderStore.registerRenderer
     */
    registerAll(...components) {
        const registerItem = (i) => {
            this.mapping.registerRenderer(i.component, i.type, i.property, i.topology);
        };
        for (let i = 0; i < components.length; i++) {
            if (Array.isArray(components[i])) {
                for (let j = 0; j < components[i].length; j++) {
                    registerItem(components[i][j]);
                }
            }
            else {
                registerItem(components[i]);
            }
        }
    }
    /**
     * Remove a resource from the store, when views are still rendered the resource will be re-fetched.
     *
     * @unstable
     */
    removeResource(subject, expedite = false) {
        this.api.invalidate(subject);
        this.store.removeResource(subject);
        return this.broadcastWithExpedite(expedite);
    }
    /**
     * Resets the render store mappings and the schema graph.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     */
    reset() {
        this.store = new RDFStore();
        this.schema = new Schema(this.store);
        this.mapping = new ComponentStore(this.schema);
    }
    /**
     * Get a render component for a rendering {property} on resource {subject}.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @param {SomeNode} subject
     * @param {NamedNode | NamedNode[]} predicate
     * @param {NamedNode} topology
     * @return {T | undefined}
     */
    resourcePropertyComponent(subject, predicate, topology) {
        return this.getComponentForProperty(this.store.getResourceProperties(subject, defaultNS.rdf("type")), predicate, topology || DEFAULT_TOPOLOGY);
    }
    /**
     * Get a render component for {subject}.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @param {SomeNode} subject The resource to get the renderer for.
     * @param {"rdflib".NamedNode} topology The topology to take into account when picking the renderer.
     * @return {T | undefined}
     */
    resourceComponent(subject, topology) {
        return this.getComponentForProperty(this.store.getResourceProperties(subject, defaultNS.rdf("type")), RENDER_CLASS_NAME, topology || DEFAULT_TOPOLOGY);
    }
    /** @unstable */
    shouldLoadResource(subject) {
        return (this.store.changeTimestamps[subject.sI] === 0 || this.api.isInvalid(subject))
            && !this.resourceQueue.find(([i]) => i === subject);
    }
    /**
     * Listen for data changes by subscribing to store changes.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @param registration
     * @param registration[0] Will be called with the new statements as its argument.
     * @param registration[1] Options for the callback.
     * @param registration[1].onlySubjects Only the subjects are passed when true.
     * @return function Unsubscription function.
     */
    subscribe(registration) {
        registration.subscribedAt = Date.now();
        const subjectFilter = registration.subjectFilter;
        if (typeof subjectFilter !== "undefined" && subjectFilter.length > 0) {
            for (let i = 0, len = subjectFilter.length; i < len; i++) {
                if (!this.subjectSubscriptions[subjectFilter[i].sI]) {
                    this.subjectSubscriptions[subjectFilter[i].sI] = [];
                }
                this.subjectSubscriptions[subjectFilter[i].sI].push(registration);
            }
            return () => {
                registration.markedForDelete = true;
                this.markForCleanup();
            };
        }
        this.bulkSubscriptions.push(registration);
        return () => {
            this.bulkSubscriptions.splice(this.bulkSubscriptions.indexOf(registration), 1);
        };
    }
    /** @internal */
    touch(_iri, _err) {
        this.broadcast();
        return true;
    }
    /**
     * Returns an entity from the cache directly.
     * This won't cause any network requests even if the entity can't be found.
     *
     * Note: This should only be used by render-libraries (e.g. link-lib), not by application code.
     * @param iri The SomeNode of the resource.
     * @returns The object if found, or undefined.
     */
    tryEntity(iri) {
        return this.store.statementsFor(iri);
    }
    /**
     * Broadcasts buffered to all subscribers.
     * The actual broadcast might be executed asynchronously to prevent lag.
     *
     * @param buffer Controls whether processing can be delayed until enough is available.
     * @param maxTimeout Set to 0 to execute immediately.
     * Note: This should only be used by render-libraries (e.g. link-redux), not by application code.
     */
    broadcast(buffer = true, maxTimeout = 1000) {
        if (maxTimeout !== 0 && this.currentBroadcast || this.broadcastHandle) {
            return this.currentBroadcast || Promise.resolve();
        }
        if (buffer) {
            if (this.store.workAvailable() >= 2) {
                if (this.broadcastHandle) {
                    window.clearTimeout(this.broadcastHandle);
                }
                if (this.lastPostponed === undefined) {
                    this.lastPostponed = Date.now();
                    this.broadcastHandle = window.setTimeout(() => {
                        this.broadcastHandle = undefined;
                        this.broadcast(buffer, maxTimeout);
                    }, 200);
                    return this.currentBroadcast || Promise.resolve();
                }
                else if (Date.now() - this.lastPostponed <= maxTimeout) {
                    this.broadcastHandle = window.setTimeout(() => {
                        this.broadcastHandle = undefined;
                        this.broadcast(buffer, maxTimeout);
                    }, 200);
                    return this.currentBroadcast || Promise.resolve();
                }
            }
            this.lastPostponed = undefined;
            this.broadcastHandle = undefined;
        }
        if (this.store.workAvailable() === 0) {
            return Promise.resolve();
        }
        const work = this.deltaProcessors.flatMap((dp) => dp.flush());
        const subjects = work
            .reduce((acc, w) => acc.includes(w.subject.sI) ? acc : acc.concat(w.subject.sI), []);
        const subjectRegs = subjects
            .flatMap((sI) => this.subjectSubscriptions[sI])
            .filter((reg) => reg
            && !reg.markedForDelete
            && (reg.subjectFilter
                ? reg.subjectFilter.some((s) => subjects.includes(s.sI))
                : true));
        if (this.bulkSubscriptions.length === 0 && subjectRegs.length === 0) {
            return Promise.resolve();
        }
        return this.currentBroadcast = new ProcessBroadcast({
            bulkSubscriptions: this.bulkSubscriptions.slice(),
            changedSubjects: subjects,
            subjectSubscriptions: subjectRegs,
            timeout: maxTimeout,
            work,
        }).run()
            .then(() => {
            this.currentBroadcast = undefined;
            if (this.store.workAvailable() > 0) {
                this.broadcast();
            }
        });
    }
    broadcastWithExpedite(expedite) {
        return this.broadcast(!expedite, expedite ? 0 : 500);
    }
    markForCleanup() {
        if (this.cleanupTimer) {
            return;
        }
        this.cleanupTimer = window.setTimeout(() => {
            this.cleanupTimer = undefined;
            this.subjectSubscriptions.forEach((registrations) => {
                for (let i = 0; i < registrations.length; i++) {
                    if (registrations[i].markedForDelete) {
                        registrations.splice(i, 1);
                    }
                    if (registrations.length === 0) {
                        delete registrations[i];
                    }
                }
            });
        }, 500);
    }
    scheduleResourceQueue() {
        if (this.resourceQueueHandle) {
            return;
        }
        if (typeof window.requestIdleCallback !== "undefined") {
            this.resourceQueueHandle = window.requestIdleCallback(this.processResourceQueue, { timeout: 100 });
        }
        else {
            this.resourceQueueHandle = window.setTimeout(this.processResourceQueue, 100);
        }
    }
    processResourceQueue() {
        this.resourceQueueHandle = undefined;
        const queue = this.resourceQueue;
        this.resourceQueue = [];
        if (this.bulkFetch) {
            this.api
                .getEntities(queue)
                .then(() => this.broadcast());
        }
        else {
            for (let i = 0; i < queue.length; i++) {
                try {
                    const [iri, opts] = queue[i];
                    this.getEntity(iri, opts);
                }
                catch (e) {
                    this.report(e);
                }
            }
        }
    }
}

/**
 * Binds various uris to link actions.
 *
 * @see {createStore}
 * @param catchActions {boolean} Set to true to catch all left-over actions to {LinkedRenderStore#execActionByIRI}.
 */
const linkMiddleware = (catchActions = true) => (lrs) => (next) => (action, args) => {
    if (action.value.startsWith(defaultNS.ll("data/rdflib/").value)) {
        return Promise.resolve(lrs.touch(args[0], args[1]));
    }
    if (catchActions) {
        return lrs.execActionByIRI(action, args);
    }
    return next(action, args);
};

function applyMiddleware(lrs, ...layers) {
    const storeBound = layers.map((middleware) => middleware(lrs));
    const finish = (a, _o) => Promise.resolve(a);
    return storeBound.reduceRight((composed, f) => f(composed), finish);
}
/**
 * Initializes a {LinkedRenderStore} with tied together middleware.
 * @param storeOpts Constructor arguments for the LRS.
 * @param middleware Main middleware, to be executed before the {linkMiddelware}.
 * @param trailingMiddleware Middleware to be placed after the {linkMiddleware}. Note: defining trailing middleware
 *  causes actions not to be executed via {LinkedRenderStore#execActionByIRI} anymore, this behaviour can be enabled
 *  manually in one of the defined middlewares if still desired.
 */
function createStore(storeOpts, middleware = [], trailingMiddleware = []) {
    const LRS = new LinkedRenderStore(storeOpts);
    LRS.dispatch = applyMiddleware(LRS, ...middleware, linkMiddleware(trailingMiddleware.length === 0), ...trailingMiddleware);
    return LRS;
}

class ComponentStoreTestProxy extends ComponentStore {
    publicLookup(predicate, obj, topology) {
        return this.lookup(predicate, obj, topology);
    }
}
const getBasicStore = (opts = {}) => {
    const report = (e) => { throw e; };
    const store = opts.store || new RDFStore();
    const processor = opts.processor || new DataProcessor({ report, store });
    const api = opts.api || processor;
    const schema = opts.schema || new Schema(store);
    const mapping = opts.mapping || new ComponentStoreTestProxy(schema);
    const conf = {
        api,
        mapping,
        report,
        schema,
        store,
    };
    const lrs = createStore(conf);
    return {
        dispatch: lrs.dispatch,
        forceBroadcast: () => lrs.broadcast(false, 0),
        lrs,
        mapping,
        processor,
        schema,
        store,
    };
};

/**
 * Processes linked-delta responses.
 */
function linkedDeltaProcessor(lrs) {
    return async function processLinkedDelta(response) {
        let data;
        if (response instanceof Response) {
            data = response.bodyUsed ? "" : await response.text();
        }
        else if (typeof XMLHttpRequest !== "undefined" && response instanceof XMLHttpRequest) {
            data = response.responseText;
        }
        else {
            data = response.body;
        }
        if (!data || data.length === 0) {
            return [];
        }
        const parser = new NQuadsParser(lrs.store.getInternalStore());
        const quads = parser.parseString(data);
        const expedite = response.hasOwnProperty("expedite") ? response.expedite : false;
        lrs.queueDelta(quads, expedite);
        // TODO: Resolve the statements in this request
        return [];
    };
}

/**
 * Processes a range of media types with parsers from the
 * [rdflib.js package](https://www.npmjs.com/package/rdflib).
 */
async function processRDF(response) {
    let data;
    if (response instanceof Response) {
        data = response.bodyUsed ? "" : await response.text();
    }
    else if (response instanceof XMLHttpRequest) {
        data = response.responseText;
    }
    else {
        data = response.body;
    }
    const format = getContentType(response);
    const g = graph();
    await new Promise((resolve) => {
        parse(data, g, getURL(response), format, () => {
            resolve();
        });
    });
    return g.statements;
}

const transformers = {
    linkedDeltaProcessor,
    processRDF,
};

export default LinkedRenderStore;
export { ComponentStoreTestProxy, DEFAULT_TOPOLOGY, LinkedRenderStore, RDFStore, RENDER_CLASS_NAME, Schema, allRDFPropertyStatements, allRDFValues, anyRDFValue, createStore, defaultNS, getBasicStore, getPropBestLangRaw, getTermBestLang, isDifferentOrigin, linkMiddleware, list, normalizeType, seq, toGraph, transformers };
//# sourceMappingURL=link-lib.es6.js.map
